import { getCustomDomainMapping } from '../../../utils/customDomain';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ChevronDown, ChevronLeft, ChevronRight, Ticket, ArrowRight, Menu as MenuIcon, Clock,
  Utensils, Store, Truck, MapPin, Star, Sparkles, X, Plus, Minus, ShoppingBag, Search,
} from 'lucide-react';
import MealPassPurchaseModal from './MealPassPurchaseModal';
import AddonsOfferModal from './AddonsOfferModal';
import AddToCartModal from '../../storefront/AddToCartModal';
import CateringOrderModal from '../../storefront/CateringOrderModal';
import OpsyCustomerChat from '../OpsyCustomerChat';
import SupportChatWidget from '../../support/SupportChatWidget';
import StorefrontSEO from './StorefrontSEO';
import { AddressAutocomplete } from '../../ui/address-autocomplete';
import { useCart } from '../../../contexts/CartContext';
import useStorefrontGate from '../../../hooks/useStorefrontGate';
import { loadActiveOrders, dropActiveOrder } from '../../../utils/activeOrders';
import useOpsyName from '../../../hooks/useOpsyName';
import useStorefrontFonts from '../../../hooks/useStorefrontFonts';
import { buildTokens, tokensToCssVars } from './storefrontTheme';
import './storefront.css';
// Demo fixtures — rendered ONLY in ?preview=1 (admin Design Studio iframe)
// when a catalog fetch is gated (403) or empty. Never shown to customers.
import {
  DEMO_PROGRAMS,
  DEMO_MEAL_PLANS,
  DEMO_TAGS,
  DEMO_MEAL_ITEMS,
  DEMO_MENU_CALENDAR,
  DEMO_CATERING_PACKAGES,
  DEMO_CATERING_AVAILABILITY,
} from './storefrontPreviewFixtures';

// ── Subdomain resolution ──────────────────────────────────────────────────
const getSubdomain = () => {
  if (getCustomDomainMapping()) return getCustomDomainMapping().subdomain;
  const urlParams = new URLSearchParams(window.location.search);
  const explicit = urlParams.get('tenant') || urlParams.get('subdomain');
  if (explicit) return explicit;
  const hostname = window.location.hostname;
  if (hostname.includes('.plateos.io')) return hostname.replace('.plateos.io', '');
  try {
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    if (s?.tenant_subdomain) return s.tenant_subdomain;
  } catch (_e) { /* ignore */ }
  return 'staging';
};

const getCustomerSessionToken = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    return s?.token || null;
  } catch (_e) { return null; }
};

// Blend the primary color with black for the deep hero background (75% darken).
// Falls back to a neutral if hex parsing fails.
const darkenHex = (hex, pct = 0.55) => {
  try {
    const h = (hex || '#7a1e2f').replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const d = (v) => Math.max(0, Math.min(255, Math.round(v * (1 - pct))));
    const to2 = (n) => n.toString(16).padStart(2, '0');
    return `#${to2(d(r))}${to2(d(g))}${to2(d(b))}`;
  } catch (_e) { return '#2a0a12'; }
};

// Lighten primary for the accent italic tagline (target: coral/pink like the reference)
const lightenHex = (hex, pct = 0.35) => {
  try {
    const h = (hex || '#f97316').replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const l = (v) => Math.max(0, Math.min(255, Math.round(v + (255 - v) * pct)));
    const to2 = (n) => n.toString(16).padStart(2, '0');
    return `#${to2(l(r))}${to2(l(g))}${to2(l(b))}`;
  } catch (_e) { return '#f97316'; }
};

// ── Reusable animated section wrapper ────────────────────────────────────
// IMPORTANT: spreads `...rest` so callers can pass `style`, `data-testid`, etc.
// Previously these were silently dropped — which caused the punchline & closing
// sections to lose their dark background + light text, leaving invisible copy
// on cream (v4.2.8 contrast fix).
/**
 * v5.12 — a plain-language "you are here" badge for each storefront section.
 *
 * Merchants write their own decorative headlines ("A few things you'll love"),
 * so visitors who tapped "Catering" couldn't tell they'd arrived. This badge
 * always names the section in the brand colour, above the custom copy.
 */
const SectionLabel = ({ children, color, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4 text-[11px] font-semibold tracking-[0.18em] uppercase"
    style={{ background: `${color}1A`, color }}
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} aria-hidden="true" />
    {children}
  </span>
);

const Section = ({ children, className = '', id, ...rest }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-120px' }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.section>
);

// ── Delivery checker widget (v4.13) ──────────────────────────────────────
// "Do we deliver to you?" — customer-facing lookup against the tenant's
// Delivery Zones. Placement + all copy are Design-Studio-controlled
// (show_delivery_checker / delivery_checker_* on the brand config, so live
// preview works with zero protocol changes). In ?preview=1 it short-circuits
// to a canned sample result (the check endpoint is storefront-gated and the
// designer's tenant may have no zones).
const _DC_DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

// Pass 2 — ONE payload rule for every checker surface (the hero pill, the
// header pill, the section): bare postal input (a full CA postal, an FSA
// prefix like "L4C", or a ZIP/ZIP prefix) goes as postal_code so the
// server's prefix matcher answers directly; a picked suggestion carries
// its coordinates + postal; anything else is an address.
export const buildDeliveryCheckBody = (q, place, storeSlug) => {
  const looksLikePostalOnly =
    /^[A-Za-z]\d[A-Za-z]\s?(?:\d[A-Za-z]\d)?$/.test(q) || /^\d{3,5}(?:-\d{4})?$/.test(q);
  const picked = !looksLikePostalOnly && place ? place : null;
  return {
    address: looksLikePostalOnly ? undefined : q,
    postal_code: looksLikePostalOnly ? q : (picked?.postal_code || undefined),
    lat: picked?.latitude ?? undefined,
    lng: picked?.longitude ?? undefined,
    store_slug: storeSlug || undefined,
  };
};

// The checker PILL — under the hero headline and, once the visitor has
// scrolled past the hero, in the header. Same endpoint and payload rule as
// the section; the verdict is one line (zone + days), never invented.
// `state` / `setState` are LIFTED to the page: the hero pill and the header
// pill are one question, so a check finished in the hero shows in the
// header (r3: each pill used to keep its own query and verdict).
const HeroDeliveryChecker = ({ copy, subdomain, storeSlug, isPreview, compact = false, onOrder, state, setState }) => {
  const { query = '', place = null, result = null } = state || {};
  const [checking, setChecking] = useState(false);
  const patch = (next) => setState((prev) => ({ ...(prev || {}), ...next }));
  const setQuery = (q) => patch({ query: q });
  const setPlace = (p) => patch({ place: p });
  const setResult = (r) => patch({ result: r });
  const run = async () => {
    const q = query.trim();
    if (!q || checking) return;
    setChecking(true);
    setResult(null);
    try {
      if (isPreview) {
        await new Promise((r) => setTimeout(r, 350));
        setResult({ enabled: true, deliverable: true, method: 'demo',
                    matched_zones: [{ name: 'Sample Zone', delivery_days: ['mon', 'wed', 'fri'] }] });
        return;
      }
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/storefront/delivery-check/${subdomain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildDeliveryCheckBody(q, place, storeSlug)),
      });
      if (!res.ok) throw new Error(`delivery check failed (${res.status})`);
      setResult(await res.json());
    } catch (_e) {
      setResult({ error: true });
    } finally {
      setChecking(false);
    }
  };
  const verdict = (() => {
    if (!result) return null;
    if (result.error) return { tone: 'warn', text: 'Something went wrong checking that address — please try again.' };
    if (result.enabled === false) return { tone: 'ok', text: copy.deliveryCheckerSuccess };
    if (result.method === 'unresolved') {
      return { tone: 'warn', text: result.message || "We couldn't locate that address — try adding your city and postal code." };
    }
    if (result.deliverable) {
      const z = (result.matched_zones || [])[0];
      const days = z && Array.isArray(z.delivery_days) && z.delivery_days.length
        ? z.delivery_days.map((d) => _DC_DAY_LABELS[d] || d).join(' / ') : '';
      return { tone: 'ok', text: copy.deliveryCheckerSuccess, detail: [z?.name, days].filter(Boolean).join(' · ') };
    }
    return { tone: 'no', text: copy.deliveryCheckerFailure };
  })();
  const tid = compact ? 'mealpass-store-header-checker' : 'mealpass-store-hero-checker';
  return (
    <div className={`relative ${compact ? 'w-full' : 'w-full max-w-xl'}`} data-testid={tid}>
      <div
        className={`sf-checker ${compact ? 'sf-checker-sm' : ''}`}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.defaultPrevented) run(); }}
      >
        <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--sf-primary)' }} aria-hidden="true" />
        <AddressAutocomplete
          value={query}
          onChange={(text) => { setQuery(text); setPlace(null); }}
          onSelect={(placeData) => {
            setQuery(placeData.address || placeData.display_name || '');
            setPlace(placeData);
          }}
          placeholder={compact ? 'Postal code or address' : 'Enter your postal code or address'}
          className="h-auto border-0 bg-transparent px-0 py-2 text-[15px] shadow-none focus-visible:ring-0"
        />
        <button
          type="button"
          onClick={run}
          disabled={checking || !query.trim()}
          data-testid={`${tid}-submit`}
          className={`sf-btn sf-btn-primary flex-shrink-0 ${compact ? '!py-2 !px-3.5 text-[13px]' : '!py-3 !px-5'}`}
        >
          {checking ? 'Checking…' : copy.deliveryCheckerCTA}
        </button>
      </div>
      {verdict && (
        <div
          className={`sf-checker-note mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 ${compact ? 'sf-card px-4 py-3 absolute left-0 right-0 top-full z-50' : ''}`}
          data-testid={`${tid}-result`}
          data-tone={verdict.tone}
        >
          <span className="font-semibold" style={{ color: verdict.tone === 'ok' ? 'var(--sf-primary)' : verdict.tone === 'no' ? '#b42318' : '#9a6700' }}>
            {verdict.text}
          </span>
          {verdict.detail && <span style={{ color: 'var(--sf-ink-muted)' }}>{verdict.detail}</span>}
          {verdict.tone === 'ok' && onOrder && (
            <button type="button" onClick={onOrder} className="font-semibold underline underline-offset-4" style={{ color: 'var(--sf-ink)' }}>
              Order now
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const DeliveryCheckerSection = ({ copy, primary, subdomain, storeSlug, isPreview }) => {
  const [query, setQuery] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  // v4.15 — place details from a picked autocomplete suggestion. lat/lng +
  // postal ride into the delivery-check POST (the server skips geocoding
  // when it has coordinates). Any manual edit invalidates the selection.
  const [place, setPlace] = useState(null);

  const runCheck = async () => {
    const q = query.trim();
    if (!q || checking) return;
    setChecking(true);
    setResult(null);
    try {
      if (isPreview) {
        await new Promise((r) => setTimeout(r, 350));
        setResult({
          enabled: true, deliverable: true, method: 'demo',
          matched_zones: [{ name: 'Sample Zone', delivery_days: ['mon', 'wed', 'fri'],
                            time_windows: [{ start: '11:00', end: '14:00' }] }],
          time_estimate: { estimated_completion_time: '12:40 PM',
                           observed_earliest_time: '11:05 AM', observed_latest_time: '2:10 PM' },
          message: 'Sample result — the live storefront checks your real delivery zones.',
        });
        return;
      }
      const base = process.env.REACT_APP_BACKEND_URL || '';
      // Bare postal input (full CA postal, an FSA prefix like "L4C", or a
      // ZIP/ZIP prefix) goes as postal_code so the server's prefix matcher
      // answers directly — sent as an address it would just fail geocoding.
      const looksLikePostalOnly =
        /^[A-Za-z]\d[A-Za-z]\s?(?:\d[A-Za-z]\d)?$/.test(q) || /^\d{3,5}(?:-\d{4})?$/.test(q);
      // A picked suggestion carries exact coordinates + postal from the
      // place-details proxy — postal-only input keeps the bare prefix path.
      const picked = !looksLikePostalOnly && place ? place : null;
      const res = await fetch(`${base}/api/storefront/delivery-check/${subdomain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: looksLikePostalOnly ? undefined : q,
          postal_code: looksLikePostalOnly ? q : (picked?.postal_code || undefined),
          lat: picked?.latitude ?? undefined,
          lng: picked?.longitude ?? undefined,
          store_slug: storeSlug || undefined,
        }),
      });
      if (!res.ok) throw new Error(`delivery check failed (${res.status})`);
      setResult(await res.json());
    } catch (_e) {
      setResult({ error: true });
    } finally {
      setChecking(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;
    // v5.27.2 — the historical delivery estimate card. Rendered by BOTH
    // success branches below (zones-enabled and zones-disabled answer
    // through different branches); null whenever the server sent no
    // estimate, so the card simply does not exist.
    const estimateCard = result.time_estimate?.estimated_completion_time ? (
      <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-left" data-testid="delivery-checker-time-estimate">
        <p className="text-xs font-semibold text-sky-950 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> Historical delivery estimate
        </p>
        <p className="mt-1 text-sm text-sky-900">
          Usually completed around{' '}
          <span className="text-lg font-bold text-sky-800">{result.time_estimate.estimated_completion_time}</span>
        </p>
        <p className="text-[11px] text-sky-900/70">
          Observed range: {result.time_estimate.observed_earliest_time}–{result.time_estimate.observed_latest_time}
        </p>
      </div>
    ) : null;
    if (result.error) {
      return (
        <p className="mt-4 text-sm text-amber-700" data-testid="delivery-checker-error">
          Something went wrong checking that address — please try again.
        </p>
      );
    }
    if (result.enabled === false) {
      return (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="delivery-checker-result">
          {copy.deliveryCheckerSuccess}
          {estimateCard}
        </div>
      );
    }
    if (result.method === 'unresolved') {
      return (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="delivery-checker-result">
          {result.message || "We couldn't locate that address — try adding your city and postal code."}
        </div>
      );
    }
    if (result.deliverable) {
      return (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-4 text-left" data-testid="delivery-checker-result">
          <p className="text-sm font-semibold text-emerald-800">{copy.deliveryCheckerSuccess}</p>
          {(result.matched_zones || []).map((z, i) => (
            <div key={i} className="mt-2 text-xs text-emerald-900/80">
              <span className="font-semibold">{z.name}</span>
              {Array.isArray(z.delivery_days) && z.delivery_days.length > 0 && (
                <span> · {z.delivery_days.map((d) => _DC_DAY_LABELS[d] || d).join(' / ')}</span>
              )}
              {(z.time_windows || []).length > 0 && (
                <span> · {(z.time_windows).map((w) => `${w.start}–${w.end}${w.label ? ` (${w.label})` : ''}`).join(', ')}</span>
              )}
            </div>
          ))}
          {estimateCard}
          {result.formatted_address ? (
            <p className="mt-2 text-[11px] text-emerald-900/60">{result.formatted_address}</p>
          ) : null}
        </div>
      );
    }
    return (
      <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800" data-testid="delivery-checker-result">
        {copy.deliveryCheckerFailure}
      </div>
    );
  };

  return (
    <Section id="delivery-checker" className="sf-section sf-tint" data-testid="mealpass-store-delivery-checker">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 text-center">
        <div className="sf-section-head !mb-8">
          <span className="sf-eyebrow">{copy.deliveryCheckerEyebrow}</span>
          <h2 className="sf-h2">{copy.deliveryCheckerHeadline}</h2>
          <p>{copy.deliveryCheckerSubtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {/* v4.15 — backend-proxied Google autocomplete (Nominatim fallback);
              Enter still runs the check UNLESS the dropdown consumed it
              (suggestion selection preventDefaults, so we skip those). */}
          <div
            className="flex-1 text-left"
            data-testid="delivery-checker-input"
            style={{ '--tw-ring-color': primary }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.defaultPrevented) runCheck(); }}
          >
            <AddressAutocomplete
              value={query}
              onChange={(text) => { setQuery(text); setPlace(null); }}
              onSelect={(placeData) => {
                setQuery(placeData.address || placeData.display_name || '');
                setPlace(placeData);
              }}
              placeholder="Street address or postal code"
              className="h-auto rounded-full border-neutral-300 bg-white px-5 pr-10 py-3 text-sm text-neutral-900 shadow-none focus-visible:ring-2"
            />
          </div>
          <button
            type="button"
            onClick={runCheck}
            disabled={checking || !query.trim()}
            data-testid="delivery-checker-submit"
            className="sf-btn sf-btn-primary disabled:opacity-50"
          >
            {checking ? 'Checking…' : copy.deliveryCheckerCTA}
          </button>
        </div>
        {renderResult()}
      </div>
    </Section>
  );
};

// ── Floating decorative icon (drifts + rotates continuously) ─────────────
const FloatingDecoration = ({ Icon, size, top, left, right, delay, duration, rotateFrom, rotateTo, opacity = 0.28 }) => (
  <motion.div
    aria-hidden="true"
    className="pointer-events-none absolute"
    style={{ top, left, right, opacity }}
    initial={{ rotate: rotateFrom, y: 0 }}
    animate={{ rotate: rotateTo, y: [0, -12, 0] }}
    transition={{
      rotate: { duration: duration || 22, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: delay || 0 },
      y: { duration: 4.5, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: delay || 0 },
    }}
  >
    <Icon style={{ width: size, height: size, color: 'currentColor' }} strokeWidth={1.4} />
  </motion.div>
);

// ── Announcement marquee (top ticker) ────────────────────────────────────
// Pass 2 — a one-line announcement bar in the brand colour (the scrolling
// uppercase marquee read as a ticker, not a promise). Phones show the first
// line; wider screens show them all.
const AnnouncementMarquee = ({ items, bg = '#111', color = '#fff' }) => (
  <div className="sf-announce" style={{ background: bg, color }} data-testid="mealpass-store-announcement">
    <span className="sm:hidden">{items[0]}</span>
    <span className="hidden sm:inline">
      {items.map((item, i) => (
        <span key={i}>
          {i > 0 && <span aria-hidden="true" className="mx-3 opacity-60">✦</span>}
          {item}
        </span>
      ))}
    </span>
  </div>
);

// v4.12 — reserved single-segment route words that are NOT store slugs. A
// stray URL like /customers/cart (e.g. a bad checkout cancel redirect) matches
// /customers/:storeSlug with storeSlug="cart" and would scope every catalog
// fetch to a non-existent store → empty plans/passes. Treat these as "no slug"
// so the storefront falls back to subdomain resolution and loads normally.
const RESERVED_SLUGS = new Set([
  'cart', 'checkout', 'login', 'dashboard', 'plans', 'meal-passes',
  'reset-password', 'theme', 'success',
  // v4.25 — dedicated SEO pages + single-segment section words. These are
  // real routes (about/contact) or on-page anchors (catering/passes); either
  // way they must never be parsed as a store slug.
  'about', 'contact', 'catering', 'passes',
]);

const MealPassProgramStore = ({ darkMode = false }) => {
  const { storeSlug: rawStoreSlug } = useParams();
  const storeSlug = RESERVED_SLUGS.has(rawStoreSlug) ? undefined : rawStoreSlug;
  const navigate = useNavigate();
  const { cartItems, addCateringToCart } = useCart();
  const [programs, setPrograms] = useState([]);
  const [branding, setBranding] = useState(null);
  const [tenantMeta, setTenantMeta] = useState(null);
  const [wallet, setWallet] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [mealItems, setMealItems] = useState([]);
  // v5.8 — count of à-la-carte ORDERABLE items (the /order page's for_order feed,
  // which can differ from the storefront display menu). Gates the "Order Now" CTA
  // so it appears whenever the store can take an online order — never opening an
  // empty order page.
  const [orderableItemCount, setOrderableItemCount] = useState(0);
  // v5.12 — the ACTUAL orderable ids, so "Add to cart" only appears on dishes
  // the /order rail will accept (the display menu is a superset).
  const [orderableIds, setOrderableIds] = useState(() => new Set());
  // {item_id: "…is out of stock right now."} — the SERVER's words, so the
  // storefront explains a refusal instead of just disabling a button.
  const [unavailableReasons, setUnavailableReasons] = useState({});
  // v5.9 — this store's ordering hours (only gates when the merchant enabled them).
  const [orderHours, setOrderHours] = useState(null);   // {enabled, is_open, opens_time}
  const [menuCalendar, setMenuCalendar] = useState([]);  // date-mode entries with dates + linked meal-item price
  const [cateringPackages, setCateringPackages] = useState([]);  // v4.24 catering section
  const [cateringAvailability, setCateringAvailability] = useState(null);  // lead time + event hours
  const [cateringDishImages, setCateringDishImages] = useState({});  // {item_id: {image_url, image_source}}
  const [selectedCateringPackage, setSelectedCateringPackage] = useState(null);  // CateringOrderModal
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  // v5.12 — gallery lightbox (the photo tiles are clickable now)
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // v5.13.1 — LIVE ORDER STATUS on the website (founder: "show the order status
  // on the website too if the user navigates back — keep it persistent"). Reads
  // the SAME plateos_active_orders_{tenant} entries the /order page writes,
  // verifies against the public /track endpoint, and shows a floating chip with
  // the live stage; tapping it opens the tracking screen on /order.
  const [activeOrder, setActiveOrder] = useState(null); // {entry, live} | null
  // v5.12 — à-la-carte MENU CART on the storefront itself. The storefront menu
  // used to be read-only (ordering lived only on /order); now visitors can add
  // dishes right here. Checkout hands the cart to the order page, which owns the
  // ONE server-authoritative money rail (dine_in_orders, connected account) —
  // we deliberately do NOT duplicate pricing/tax/Stripe logic here.
  const [menuCart, setMenuCart] = useState({});   // { itemId: qty }
  // v5.12 — which section the visitor is currently in (drives the active nav pill
  // + the "you are here" section header).
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedMealPlan, setSelectedMealPlan] = useState(null); // for AddToCartModal
  const [addonSubjectPlan, setAddonSubjectPlan] = useState(null); // add-on plan being configured
  // v5.10 — free (Launch) tenants show a subtle "Powered by PlateOS.io" watermark.
  const [poweredByPlateos, setPoweredByPlateos] = useState(false);
  const [showAddonsOffer, setShowAddonsOffer] = useState(false); // post-add-to-cart upsell
  const [preAddCartCount, setPreAddCartCount] = useState(0); // snapshot of cart size when modal opened
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);
  const [menuActiveCategory, setMenuActiveCategory] = useState(null); // Product carousel active tab
  const [menuSearchQuery, setMenuSearchQuery] = useState(''); // Menu search filter
  const [headerSolid, setHeaderSolid] = useState(false);
  // r3 — the header pill waits until the HERO has left the viewport (the
  // hero is full-viewport now; a 260px scroll showed both pills at once).
  const [pastHero, setPastHero] = useState(false);
  // r3 — ONE checker state shared by the hero pill and the header pill.
  const [checkerState, setCheckerState] = useState({ query: '', place: null, result: null });
  // Preview-only: true when any demo fixture was substituted for a gated/empty
  // catalog (drives the "sample data" badge). Never set outside ?preview=1.
  const [demoDataActive, setDemoDataActive] = useState(false);

  const programsAnchor = useRef(null);
  const mealPlansAnchor = useRef(null);
  const cateringAnchor = useRef(null);
  const menuAnchor = useRef(null);

  // v5.12 — SCROLL SPY. Visitors told us they couldn't tell which section they
  // were in (e.g. after tapping "Catering"). Track the section currently under
  // the header so the nav pill highlights and each section shows a "you are
  // here" heading.
  useEffect(() => {
    const anchors = [
      ['passes', programsAnchor],
      ['plans', mealPlansAnchor],
      ['catering', cateringAnchor],
      ['menu', menuAnchor],
    ];
    const onScroll = () => {
      let current = null;
      for (const [key, ref] of anchors) {
        const el = ref?.current;
        if (!el) continue;
        if (el.getBoundingClientRect().top <= 140) current = key;
      }
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // v5.12 — Escape closes the gallery lightbox.
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxIdx(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx]);
  const storyAnchor = useRef(null);
  const heroRef = useRef(null);
  // v5.8 — the sticky header's measured height drives the hero's negative
  // margin so the hero meets the marquee bottom EXACTLY (no cream/scrim gap).
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  // v5.8 — mobile "More" hamburger (collapses the secondary nav items on phones).
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Pass 2 — the phone menu is a full-screen sheet: lock the page behind it.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // r3 — a modal dialog closes on Escape, like every other one on the page
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [mobileMenuOpen]);
  const showcaseStripRef = useRef(null);
  const scrollStrip = (dir) => {
    const el = showcaseStripRef.current;
    if (el) el.scrollBy({ left: dir * Math.max(280, Math.round(el.clientWidth * 0.8)), behavior: 'smooth' });
  };
  const customerSessionToken = getCustomerSessionToken();
  const isAuthed = Boolean(customerSessionToken);

  useEffect(() => {
    const measure = () => setHeaderH(headerRef.current?.offsetHeight || 0);
    measure();
    const t = setTimeout(measure, 250);  // re-measure after fonts settle
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [isAuthed]);
  // v4.12 — per-store assistant name for the storefront chat entry points
  // (tenant resolution inside the hook works for anonymous visitors too).
  const opsyName = useOpsyName(storeSlug);

  // Detect preview mode — when embedded in the admin storefront settings iframe,
  // the parent posts config updates in real-time. Overrides the fetched branding.
  const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';

  // v4.9 — storefront access gate: when Settings → Portal → Enable Storefront
  // is OFF, this whole page redirects to the customer login (fail-closed;
  // preview iframe bypasses). Restores the gate lost with CustomerStorefront.
  const gate = useStorefrontGate(getSubdomain(), storeSlug);

  // Sticky header: fade to solid after scrolling past hero
  useEffect(() => {
    const onScroll = () => {
      setHeaderSolid(window.scrollY > 260);
      const hero = heroRef.current;
      setPastHero(hero ? hero.getBoundingClientRect().bottom <= (headerRef.current?.offsetHeight || 72) : window.scrollY > 260);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ─── Live-preview postMessage listener ──────────────────────────────────
  // The admin StorefrontSettings page embeds this component in an iframe and
  // posts the current form state as the merchant types. We override branding
  // in place so the merchant sees changes instantly — no save required.
  useEffect(() => {
    if (!isPreview) return;
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'MEAL_PASS_PREVIEW_CONFIG') return;
      if (data.branding && typeof data.branding === 'object') {
        setBranding(data.branding);
        setTenantMeta((prev) => prev || { name: data.branding.brand_name, subdomain: getSubdomain() });
        // Ensure loading state clears once we have preview data even if the
        // backend fetch hasn't completed yet.
        setLoading(false);
      }
    };
    window.addEventListener('message', onMessage);
    // READY handshake (v4.9.2) — ask the designer for the current config as
    // soon as the listener exists. Fixes the first-load race where the
    // parent's iframe-onLoad post fired before this listener was attached
    // (or before the parent had fetched its saved config), leaving default
    // colors until a manual refresh.
    try { window.parent?.postMessage({ type: 'MEAL_PASS_PREVIEW_READY' }, '*'); } catch { /* noop */ }
    return () => window.removeEventListener('message', onMessage);
  }, [isPreview]);

  // Subtle hero parallax on scroll
  const { scrollY } = useScroll();
  const heroTitleY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroDecorY = useTransform(scrollY, [0, 500], [0, -70]);
  // Hero background image parallax — slow upward drift as you scroll
  const heroBgY = useTransform(scrollY, [0, 800], [0, -120]);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const subdomain = getSubdomain();
      const base = process.env.REACT_APP_BACKEND_URL;
      // v5.8 — per-channel visibility: a logged-in shopper sees the customer_dashboard
      // channel; an anonymous visitor sees the storefront channel. (The for_order
      // feed below is the ordering_page channel, resolved server-side.)
      const feedChannel = isAuthed ? 'customer_dashboard' : 'storefront';
      // v4.9 per-store mode: when the route carries a storeSlug, scope every
      // public fetch to that store. Handles URLs that already have a query.
      const withStoreSlug = (url) => {
        if (!storeSlug) return url;
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}store_slug=${encodeURIComponent(storeSlug)}`;
      };
      // Preview-only (?preview=1, admin Design Studio iframe): when a catalog
      // fetch is gated (403 storefront-disabled) or returns an empty list, we
      // substitute demo fixtures so the designer sees the storefront exactly
      // as customers would — never a hollow skeleton. Real visitors never see
      // these: every substitution is behind `isPreview`.
      const res = await fetch(withStoreSlug(`${base}/api/meal-pass/public/${subdomain}/programs`));
      if (!res.ok && !isPreview) {
        setError(`Could not load meal passes (${res.status})`);
        setPrograms([]);
        return;
      }
      if (!res.ok) {
        // Preview: gated/failed programs fetch → demo passes; keep going so the
        // remaining sections load (or substitute) too instead of bailing out.
        setPrograms(DEMO_PROGRAMS);
        setDemoDataActive(true);
      } else {
        const data = await res.json();
        const fetchedPrograms = data.programs || [];
        if (isPreview && !fetchedPrograms.length) {
          setPrograms(DEMO_PROGRAMS);
          setDemoDataActive(true);
        } else {
          setPrograms(fetchedPrograms);
        }
        setBranding(data.branding || null);
        setTenantMeta(data.tenant_meta || null);
      }

      // Preview bookkeeping: demo tags must only accompany demo meal plans —
      // their meal_plan_ids reference the demo plan ids, so mixing demo tags
      // with a real catalog would break the filter chips.
      let usedDemoPlans = false;

      // Fetch active meal plans from the existing storefront endpoint (public, no auth).
      // These render alongside meal passes as a unified restaurant landing page.
      try {
        const mpRes = await fetch(withStoreSlug(`${base}/api/storefront/meal-plans/${subdomain}?channel=${feedChannel}`));
        if (mpRes.ok) {
          const mpj = await mpRes.json();
          // Filter out the system Custom placeholder — never a customer-facing offer.
          // Also filter out add-ons (they are offered as an upsell step AFTER the
          // main plan is added to cart, not as primary cards on the storefront).
          const plans = (mpj.meal_plans || []).filter(
            (p) => p.id !== 'system-custom-plan' && p.is_active !== false
          );
          if (isPreview && !plans.length) {
            setMealPlans(DEMO_MEAL_PLANS);
            usedDemoPlans = true;
            setDemoDataActive(true);
          } else {
            setMealPlans(plans);
          }
        } else if (isPreview) {
          setMealPlans(DEMO_MEAL_PLANS);
          usedDemoPlans = true;
          setDemoDataActive(true);
        }
      } catch (_e) { /* optional enrichment */ }

      // Fetch tags for filtering the meal-plan/menu sections.
      try {
        const tRes = await fetch(withStoreSlug(`${base}/api/storefront/tags/${subdomain}`));
        if (tRes.ok) {
          const tj = await tRes.json();
          // Preview: when the plans are demo, the tags must be the matching demo
          // set (real tags' meal_plan_ids can't reference demo plan ids).
          setTags(usedDemoPlans ? DEMO_TAGS : (tj.tags || []));
        } else if (isPreview && usedDemoPlans) {
          setTags(DEMO_TAGS);
        }
      } catch (_e) { /* optional */ }

      // Fetch à-la-carte menu items (rendered only if merchant enables menu module).
      try {
        const miRes = await fetch(withStoreSlug(`${base}/api/storefront/meal-items/${subdomain}?channel=${feedChannel}`));
        if (miRes.ok) {
          const mij = await miRes.json();
          const items = (mij.meal_items || []).filter((m) => m.is_active !== false);
          if (isPreview && !items.length) {
            setMealItems(DEMO_MEAL_ITEMS);
            setDemoDataActive(true);
          } else {
            setMealItems(items);
          }
        } else if (isPreview) {
          setMealItems(DEMO_MEAL_ITEMS);
          setDemoDataActive(true);
        }
      } catch (_e) { /* optional */ }

      // v5.8 — orderable-items availability for the "Order Now" CTA. The /order page
      // orders from the for_order feed (tenant-wide + priced items, resolved the same
      // way the order rail does), which can differ from the display menu above.
      try {
        const ordRes = await fetch(withStoreSlug(`${base}/api/storefront/meal-items/${subdomain}?for_order=1`));
        if (ordRes.ok) {
          const oj = await ordRes.json();
          const ordItems = oj.meal_items || [];
          setOrderableItemCount(ordItems.length);
          // FOUNDER REDESIGN — out-of-stock is SYSTEM-WIDE: an 86'd dish stays
          // LISTED on the display menu but loses its Add-to-cart here too (the
          // money rail refuses it regardless; this keeps the button honest).
          // Review round 4: this filtered `out_of_stock` only, so a dish
          // OUTSIDE its availability window still showed an enabled Add and
          // the customer met the refusal at checkout instead. The feed now
          // ships a server-computed `orderable` (tenant tz, same verdict the
          // money rail reaches) — trust that, never the browser clock.
          // `orderable !== false` keeps older payloads behaving as before.
          setOrderableIds(new Set(
            ordItems.filter((it) => it.orderable !== false && !it.out_of_stock)
              .map((it) => it.id)));
          setUnavailableReasons(Object.fromEntries(
            ordItems.filter((it) => it.unavailable_reason)
              .map((it) => [it.id, it.unavailable_reason])));
        }
      } catch (_e) { /* optional */ }

      // v5.9 — this store's ordering hours (per-store), so the storefront can show
      // "Opens for orders at <time>" instead of hiding anything when closed.
      try {
        const shRes = await fetch(withStoreSlug(`${base}/api/storefront/settings/${subdomain}`));
        if (shRes.ok) {
          const sh = await shRes.json();
          setOrderHours({
            enabled: !!sh.store_hours_enabled,
            is_open: sh.is_open !== false,
            opens_time: sh.next_open?.time || null,
          });
          setPoweredByPlateos(!!sh.powered_by_plateos);
        }
      } catch (_e) { /* optional */ }

      // Fetch menu calendar (date-mode source) — scheduled entries for the current
      // week enriched with linked meal_item name/price/image. Used when merchant
      // selects menu_mode="date" in storefront settings.
      try {
        const mcRes = await fetch(withStoreSlug(`${base}/api/storefront/menu-calendar/${subdomain}`));
        if (mcRes.ok) {
          const mcj = await mcRes.json();
          const entries = mcj.entries || [];
          if (isPreview && !entries.length) {
            setMenuCalendar(DEMO_MENU_CALENDAR);
            setDemoDataActive(true);
          } else {
            setMenuCalendar(entries);
          }
        } else if (isPreview) {
          setMenuCalendar(DEMO_MENU_CALENDAR);
          setDemoDataActive(true);
        }
      } catch (_e) { /* optional */ }

      // Fetch catering packages (v4.24) — rendered only when the merchant
      // opts in (brand.catering_enabled === true) AND packages exist.
      try {
        const cRes = await fetch(withStoreSlug(`${base}/api/catering/public/${subdomain}/packages`));
        if (cRes.ok) {
          const cj = await cRes.json();
          const pkgs = cj.packages || [];
          if (isPreview && !pkgs.length) {
            setCateringPackages(DEMO_CATERING_PACKAGES);
            setCateringAvailability(DEMO_CATERING_AVAILABILITY);
            setDemoDataActive(true);
          } else {
            setCateringPackages(pkgs);
            setCateringAvailability(cj.availability || null);
            if (cj.dish_images) setCateringDishImages(cj.dish_images);
          }
        } else if (isPreview) {
          setCateringPackages(DEMO_CATERING_PACKAGES);
          setCateringAvailability(DEMO_CATERING_AVAILABILITY);
          setDemoDataActive(true);
        }
      } catch (_e) { /* optional */ }

      if (customerSessionToken) {
        try {
          const w = await fetch(`${base}/api/meal-pass/customer/wallet`, {
            headers: { Authorization: `Bearer ${customerSessionToken}` },
          });
          if (w.ok) {
            const wj = await w.json();
            setWallet(wj.accounts || wj.wallet || []);
          }
        } catch (_e) { /* optional */ }
      }
    } catch (err) {
      console.error('Meal pass store load error:', err);
      if (isPreview) {
        // Preview: a hard network failure happens before any optional section
        // loads — populate every section with demo fixtures so the design
        // canvas never renders hollow.
        setPrograms(DEMO_PROGRAMS);
        setMealPlans(DEMO_MEAL_PLANS);
        setTags(DEMO_TAGS);
        setMealItems(DEMO_MEAL_ITEMS);
        setMenuCalendar(DEMO_MENU_CALENDAR);
        setCateringPackages(DEMO_CATERING_PACKAGES);
        setCateringAvailability(DEMO_CATERING_AVAILABILITY);
        setDemoDataActive(true);
      } else {
        setError('Could not connect to the server');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (ref) => {
    if (ref?.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBuy = (program) => {
    setPreAddCartCount(cartItems?.length || 0);
    setSelectedProgram(program);
    setShowPurchaseModal(true);
  };

  const openWallet = () => {
    const dest = storeSlug ? `/customers/${storeSlug}/dashboard` : '/customers/dashboard';
    navigate(`${dest}#meal-passes`);
  };

  const brand = branding || {
    brand_name: tenantMeta?.name || 'Meal Passes',
    primary_color: '#7a1e2f',
    hero_title: 'Buy meals in advance. Save every time.',
    hero_subtitle: 'Redeem anytime — dine in, pickup, or delivery',
    show_savings_calculator: true,
    faqs: [],
    tagline: 'always fresh',
  };

  // v5.66 — ONE token set for every storefront surface (page paper, ink,
  // primary, accent, dark slab, radius, the three fonts), exposed as CSS
  // variables on the page shell so Design Studio restyles the whole page
  // from one form. The chosen fonts are LOADED here — the editor used to
  // offer families nothing fetched, so every choice fell back silently.
  const tokens = buildTokens(brand);
  useStorefrontFonts([tokens.headingFont, tokens.bodyFont, tokens.scriptFont]);
  const primary = tokens.primary;
  // Hero backdrop = a lightly-darkened primary (keeps brand color legible + rich).
  const heroBg = darkenHex(primary, 0.15);
  // Deep dark accent surface for punchline + closing slabs. Aggressively darkened
  // so it's always near-black regardless of the merchant's brand color — otherwise
  // a bright primary (e.g. orange) leaves cream text unreadable on the same surface.
  const deepDark = darkenHex(primary, 0.78);
  // Accent color: honor explicit merchant override, else derive coral/pink from primary.
  const accent = tokens.accent;

  // ── Design Studio v2: per-section style overrides ──────────────────
  const sectionStyles = brand.section_styles || {};
  const getSectionStyle = (sectionId) => {
    // v5.66 — only the keys the merchant SET: an `undefined` backgroundColor
    // spread over a section's own default erased it (how-it-works, footer)
    // and React warned about the shorthand/longhand mix on every render.
    const s = sectionStyles[sectionId] || {};
    const out = {};
    if (s.bg_color) out.backgroundColor = s.bg_color;
    if (s.text_color) out.color = s.text_color;
    return out;
  };
  // Font overrides (Design Studio v2)
  const headingFontStack = tokens.headingStack;
  const bodyFontStack = tokens.bodyStack;
  // The handwritten accent phrase used in every headline — a token now, not
  // a literal `Caveat` at a dozen sites (Design Studio: "Accent font").
  const scriptFontStack = tokens.scriptStack;
  // Pass 2 — every headline's accent phrase: coloured (default) or handwritten.
  const phraseCls = tokens.accentStyle === 'script' ? 'sf-accent-phrase' : 'sf-highlight';

  // Hero CTA anchor — merchant picks which section the CTA scrolls to.
  const heroCTAAnchorId = brand.hero_cta_anchor || null;
  const heroCTAStyle = brand.hero_cta_style || 'pill';

  // Determine the primary offering anchor for hero CTAs — depends on which
  // modules the merchant has enabled. Passes → plans → menu, in that order.
  // If the merchant set an explicit hero_cta_anchor, use that instead.
  const primaryOfferAnchor = () => {
    if (heroCTAAnchorId) {
      return { current: document.getElementById(heroCTAAnchorId) };
    }
    if (brand.mealpass_enabled !== false) return programsAnchor;
    if (brand.mealplans_enabled !== false && mealPlans.filter((p) => !p.is_meal_addon && !p.is_delivery_addon).length > 0) return mealPlansAnchor;
    if (brand.menu_enabled === true && mealItems.length > 0) return menuAnchor;
    return programsAnchor; // fallback
  };
  const primaryOfferLabel = () => {
    if (brand.mealpass_enabled !== false) return brand.hero_cta_primary || 'Browse passes';
    if (brand.mealplans_enabled !== false && mealPlans.filter((p) => !p.is_meal_addon && !p.is_delivery_addon).length > 0) return 'Browse plans';
    if (brand.menu_enabled === true && mealItems.length > 0) return 'See the menu';
    return brand.hero_cta_primary || 'Browse passes';
  };

  // ─── Copy resolution helpers ───────────────────────────────────────────
  // Every text field on the storefront prefers the merchant's config, then
  // falls back to the sensible defaults from v4.2.7. This lets merchants
  // fully customize every heading, italic script, eyebrow, and body without
  // touching code.
  const copy = {
    heroTitle: brand.hero_title || brand.brand_name || 'Meal Passes',
    heroItalic: brand.hero_italic || 'meal passes',
    heroSubtitle: brand.hero_subtitle || 'Buy today, redeem forever. Credits never expire, and every dollar stays with the kitchen you love.',
    heroCTA1: brand.hero_cta_primary || 'Browse passes',
    heroCTA2: brand.hero_cta_secondary || 'Our story',
    // Story
    storyEyebrow: brand.story_eyebrow || 'A small ritual',
    storyHeadline: brand.story_headline || 'A meal you already own,',
    storyHeadlineItalic: brand.story_headline_italic || 'waiting on you.',
    storyBodyHtml: brand.story_body_html || brand.about_html || null,
    storyImage: brand.story_image_url || brand.hero_image_url || null,
    // Steps
    stepsEyebrow: brand.steps_eyebrow || 'Three simple steps',
    stepsHeadline: brand.steps_headline || 'How Meal Passes',
    stepsHeadlineItalic: brand.steps_headline_italic || 'work',
    steps: (Array.isArray(brand.steps) && brand.steps.length) ? brand.steps : [
      { number: '01', title: 'Choose your pass', body: 'Pick from starter, popular, or best-value bundles. Every credit counts as one meal.' },
      { number: '02', title: 'Buy once, own forever', body: "One checkout. No subscriptions. Credits never expire — they're truly yours." },
      { number: '03', title: 'Redeem how you like', body: 'Dine in, take out, or delivery. Show your wallet at the counter or in-app. Done.' },
    ],
    // Showcase (v5.66) — the dish strip under the hero
    showcaseEyebrow: brand.showcase_eyebrow || 'From the kitchen',
    showcaseHeadline: brand.showcase_headline || 'Straight from',
    showcaseHeadlineItalic: brand.showcase_headline_italic || 'our kitchen',
    // Plans / subscriptions (v5.66 — the heading was hardcoded English)
    plansEyebrow: brand.plans_eyebrow || 'Meal plans',
    plansHeadline: brand.plans_headline || 'Meals delivered on a',
    plansHeadlineItalic: brand.plans_headline_italic || 'rhythm.',
    plansSubheadline: brand.plans_subheadline || 'Pick a plan and the days you eat with us. Pause or skip whenever you need to.',
    // Programs
    programsEyebrow: brand.programs_eyebrow || 'Available now',
    programsHeadline: brand.programs_headline || 'Choose your',
    programsHeadlineItalic: brand.programs_headline_italic || 'pass',
    programsSubheadline: brand.programs_subheadline || `Every pass is honored directly by ${brand.brand_name || 'us'}. Credits never expire.`,
    // Gallery
    galleryEyebrow: brand.gallery_eyebrow || 'Taste the kitchen',
    galleryHeadline: brand.gallery_headline || "A few things you'll",
    galleryHeadlineItalic: brand.gallery_headline_italic || 'love.',
    galleryItems: Array.isArray(brand.gallery_items) ? brand.gallery_items : [],
    // Testimonials
    testEyebrow: brand.testimonials_eyebrow || 'Reviews',
    testHeadline: brand.testimonials_headline || 'Regulars, made',
    testHeadlineItalic: brand.testimonials_headline_italic || 'loyal.',
    testimonials: (Array.isArray(brand.testimonials) && brand.testimonials.length) ? brand.testimonials : [
      { name: 'Priya S.', quote: 'I skip the app fees and support a place I love. Best decision.', rating: 5 },
      { name: 'Marco D.', quote: 'Bought a 10-pack. Two months in and still not close to using them all — no rush, no stress.', rating: 5 },
      { name: 'Emily R.', quote: 'The convenience of just walking in and having lunch covered. Feels like a members-only club.', rating: 5 },
    ],
    // Punchline
    punchlineHeadline: brand.punchline_headline || 'Every meal, already',
    punchlineHeadlineItalic: brand.punchline_headline_italic || 'yours.',
    punchlineBody: brand.punchline_body || 'Buy today. Come back whenever hunger, tradition, or a bad Tuesday calls for it.',
    punchlineCTA: brand.punchline_cta || 'Pick your pass',
    // FAQ
    faqsEyebrow: brand.faqs_eyebrow || 'Details',
    faqsHeadline: brand.faqs_headline || 'Frequently',
    faqsHeadlineItalic: brand.faqs_headline_italic || 'asked',
    // Closing slab
    closingSince: brand.closing_slab_since || brand.tagline || `Since ${new Date().getFullYear()}`,
    closingHeadline: brand.closing_slab_headline || 'Come hungry.',
    closingHeadlineItalic: brand.closing_slab_headline_italic || 'Leave full.',
    // Delivery checker (v4.13)
    deliveryCheckerEyebrow: brand.delivery_checker_eyebrow || 'Delivery area',
    deliveryCheckerHeadline: brand.delivery_checker_headline || 'Do we deliver to you?',
    deliveryCheckerSubtitle: brand.delivery_checker_subtitle || 'Type your address or postal code and find out in seconds.',
    deliveryCheckerCTA: brand.delivery_checker_cta || 'Check my address',
    deliveryCheckerSuccess: brand.delivery_checker_success_text || "Great news — you're in our delivery area!",
    deliveryCheckerFailure: brand.delivery_checker_failure_text || "We don't deliver there just yet — but we're expanding fast.",
    // Catering (v4.24)
    cateringEyebrow: brand.catering_eyebrow || 'Catering',
    cateringHeadline: brand.catering_headline || 'Feed the whole',
    cateringHeadlineItalic: brand.catering_headline_italic || 'table',
    cateringSubheadline: brand.catering_subheadline || 'Packages for events big and small — built from the dishes you already love.',
    // Marquee
    marqueeItems: (Array.isArray(brand.marquee_items) && brand.marquee_items.length) ? brand.marquee_items : [
      '★ Credits never expire',
      'Meal passes · Subscriptions · Direct orders',
      'Powered by your local kitchen',
      `Support ${brand.brand_name || 'us'} directly`,
    ],
  };

  // Split the fetched meal plans into primary (subscription bundles) vs add-ons.
  // Primary plans render as main cards; add-ons are offered as a post-checkout upsell.
  const primaryMealPlans = mealPlans.filter((p) => !p.is_meal_addon && !p.is_delivery_addon);
  // Only offer add-ons that have a POSITIVE price value — a $0/unpriced add-on
  // isn't a purchasable extra and shouldn't appear in either add-on surface.
  // Price is derived the same way AddonCard renders it (first tier, else base).
  const availableAddons = mealPlans.filter((p) => {
    if (!(p.is_meal_addon || p.is_delivery_addon)) return false;
    const firstTier = (p.pricing?.tiers || [])[0] || {};
    const price = Number(firstTier.price ?? p.base_price ?? 0);
    return price > 0;
  });

  // Visibility toggles (default all true — merchant can hide sections)
  const show = {
    marquee: brand.show_marquee !== false,
    hero: brand.show_hero !== false,
    story: brand.show_story !== false,
    howItWorks: brand.show_how_it_works !== false,
    gallery: brand.show_gallery !== false && (Array.isArray(brand.gallery_items) ? brand.gallery_items.length > 0 : false),
    testimonials: brand.show_testimonials !== false,
    punchline: brand.show_punchline !== false,
    faq: brand.show_faq !== false,
    closingSlab: brand.show_closing_slab !== false,
    // Module master toggles — merchant can turn off any of the 3 offerings
    mealpassSection: brand.mealpass_enabled !== false,
    mealplansSection: brand.mealplans_enabled !== false && primaryMealPlans.length > 0,
    menuSection: brand.menu_enabled === true && (
      brand.menu_mode === 'date' ? menuCalendar.length > 0 : mealItems.length > 0
    ),
    // Delivery checker (v4.13) — explicit opt-in (model default false) so
    // existing storefronts don't suddenly grow a widget.
    deliveryChecker: brand.show_delivery_checker === true,
    // v5.66 — the dish showcase strip: on unless the merchant hides it, and
    // only when there are at least three photographed things to show.
    showcase: brand.show_menu_showcase !== false,
    // Catering (v4.24) — explicit opt-in like menu_enabled, AND at least
    // one active package so we never render an empty section.
    cateringSection: brand.catering_enabled === true && cateringPackages.length > 0,
  };

  // ─── Header nav (v4.24) ─────────────────────────────────────────────
  // Section links scroll to the on-page anchor; About/Contact route to the
  // dedicated SEO pages. Section links only appear when that module is on,
  // so the nav mirrors what the storefront actually sells.
  const goToPage = (page) => {
    const base = storeSlug ? `/customers/${storeSlug}` : '/customers';
    navigate(`${base}/${page}`);
  };
  // v5.8 — the storefront "Order Now" CTA deep-links into the /order page with the
  // ONLINE source and THIS store's slug (store_slug respected end-to-end), so online
  // ordering is fully integrated on the storefront, not just a QR-only entry.
  // v5.13.1 — pick up any LIVE order placed on /order (same-origin localStorage)
  // and keep its stage fresh: verify with the public /track endpoint, drop
  // terminal/invalid entries, and poll every 15s while an order is in flight.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    let alive = true;
    let timer = null;
    // The shared module owns the key + TTL, and we let IT resolve the tenant —
    // passing getSubdomain() here would defeat the whole point, because that
    // helper has ALREADY applied its own 'staging' API fallback while the
    // /order page's ladder ends at 'main', so the two would still land in
    // different buckets (bugbot, twice). Calling with no argument runs the one
    // shared ladder on both sides, which is the only way they agree.
    const readEntries = () => loadActiveOrders();
    const dropEntry = (id) => dropActiveOrder(undefined, id);
    const tick = async () => {
      const entries = readEntries();
      if (!entries.length) { if (alive) setActiveOrder(null); return; }
      const entry = entries[0];
      try {
        const res = await fetch(`${base}/api/dine-in/orders/track?token=${encodeURIComponent(entry.token)}`);
        if (!alive) return;
        if (!res.ok) { dropEntry(entry.id); setActiveOrder(null); return; }
        const live = await res.json();
        const terminal = live.status === 'cancelled' || live.prep_state === 'completed';
        if (terminal) { dropEntry(entry.id); setActiveOrder(null); return; }
        setActiveOrder({ entry, live });
        timer = setTimeout(tick, 15000);
      } catch { /* transient — keep the last known chip */ }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  // FOUNDER BUG (2026-07-24) — CHECKOUT MUST KEEP THE TENANT IDENTITY. On a
  // non-subdomain host (localhost, apex plateos.io) the ?tenant= query is the
  // ONLY thing naming the tenant, and the checkout links used to drop it. The
  // checkout page then guessed the tenant FROM THE STORE SLUG (wrong for any
  // multi-store tenant), and its empty-cart guard bounced a Back-navigating
  // customer to a naked /customers that resolved the 'staging' default —
  // store context lost, website blocked on the wrong tenant's login. ONE
  // builder for every path to checkout, carrying only tenant/subdomain.
  const checkoutDest = (extraQs = '') => {
    const base = storeSlug ? `/customers/${storeSlug}/checkout` : '/customers/checkout';
    const p = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    for (const k of ['tenant', 'subdomain']) {
      const v = p.get(k);
      if (v) keep.set(k, v);
    }
    if (extraQs) new URLSearchParams(extraQs).forEach((v, k) => keep.set(k, v));
    const qs = keep.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const goOrderOnline = (handoffCart = null) => {
    const sub = getSubdomain();
    const parts = [];
    if (sub) parts.push(`tenant=${encodeURIComponent(sub)}`);
    if (storeSlug) parts.push(`store_slug=${encodeURIComponent(storeSlug)}`);
    parts.push('src=online');
    // v5.12 — hand the storefront menu cart to the order page, which owns the
    // server-authoritative pricing/tax/checkout rail. sessionStorage keeps the
    // URL clean and survives the navigation.
    if (handoffCart && Object.keys(handoffCart).length > 0) {
      try {
        sessionStorage.setItem('plateos_storefront_cart', JSON.stringify(handoffCart));
      } catch { /* private mode — the customer just re-picks on the order page */ }
    }
    navigate(`/order?${parts.join('&')}`);
  };

  // v5.12 — storefront menu cart helpers.
  const menuCartCount = Object.values(menuCart).reduce((s, q) => s + q, 0);
  // v5.12 (codex) — mirror the ORDER PAGE's unitPrice: take the first NON-ZERO
  // of price → base_price → cheapest priced tier. A `??` chain stops at a
  // base_price of 0, which hid "Add" on tier-priced dishes the order rail
  // happily accepts (base 0 + priced tiers is a valid, orderable shape).
  const menuItemPrice = (item) => {
    const p = Number(item?.price);
    if (p > 0) return p;
    const bp = Number(item?.base_price);
    if (bp > 0) return bp;
    const tierPrices = (item?.price_tiers || [])
      .map((t) => Number(t?.price)).filter((n) => n > 0);
    return tierPrices.length ? Math.min(...tierPrices) : 0;
  };
  // v5.14C (founder) — the website menu was SIZE-BLIND: it rendered a flat
  // "Add" and the cheapest tier's price for items that /order shows as
  // "from $X" + "Choose size", and the cart handoff then seeded the DEFAULT
  // (cheapest) size. A customer who wanted Large silently got Small.
  // Sized items now say "from $X" + "Choose size" and hand off to /order, whose
  // size sheet is the ONE place à-la-carte pricing is picked (the repo rule:
  // /order is the single money rail — this surface must not grow a second one).
  const menuSizeOptions = (item) => (Array.isArray(item?.size_options) ? item.size_options : []);
  const menuHasSizes = (item) => menuSizeOptions(item).length > 1;
  const openSizePickerOnOrderPage = (item) => {
    try {
      sessionStorage.setItem('plateos_storefront_pick_size', item.id);
    } catch { /* private mode — /order still shows the picker on add */ }
    goOrderOnline();
  };

  const bumpMenuCart = (itemId, delta) => setMenuCart((prev) => {
    const next = { ...prev };
    const q = (next[itemId] || 0) + delta;
    if (q <= 0) delete next[itemId]; else next[itemId] = q;
    return next;
  });
  const menuCartSubtotal = mealItems.reduce(
    (sum, it) => sum + (menuCart[it.id] ? menuItemPrice(it) * menuCart[it.id] : 0), 0);
  // v5.8 — the account action (Sign in / My Dashboard) lives IN the menu now.
  const loginDest = storeSlug ? `/customers/${storeSlug}/login` : '/customers/login';
  // `mobile: true` items stay inline on phones (Order Now + Passes + Subscriptions
  // + Catering); everything else collapses into the "More" hamburger.
  const navItems = [
    // Order Now shows only when the store has à-la-carte orderable items (the
    // for_order feed the /order page uses), so it's never opened empty.
    orderableItemCount > 0 && {
      key: 'order',
      // v5.9 — when this store is outside its ordering hours, keep the CTA (menu
      // stays fully browsable) but say when it opens instead of "Order Now".
      label: (orderHours?.enabled && !orderHours.is_open)
        ? (orderHours.opens_time ? `Opens ${orderHours.opens_time}` : 'Ordering closed')
        : 'Order Now',
      primary: true, mobile: true, onClick: goOrderOnline,
    },
    show.mealpassSection && { key: 'passes', label: 'Passes', mobile: true, onClick: () => scrollTo(programsAnchor) },
    show.mealplansSection && { key: 'plans', label: 'Subscriptions', mobile: true, onClick: () => scrollTo(mealPlansAnchor) },
    show.cateringSection && { key: 'catering', label: 'Catering', mobile: true, onClick: () => scrollTo(cateringAnchor) },
    show.menuSection && { key: 'menu', label: 'Menu', mobile: false, onClick: () => scrollTo(menuAnchor) },
    { key: 'about', label: 'About', mobile: false, onClick: () => goToPage('about') },
    { key: 'contact', label: 'Contact', mobile: false, onClick: () => goToPage('contact') },
    { key: 'account', label: isAuthed ? 'My Dashboard' : 'Sign in', mobile: false,
      testid: isAuthed ? 'mealpass-store-back-button' : 'mealpass-store-signin-button',
      onClick: isAuthed ? openWallet : () => navigate(loginDest) },
  ].filter(Boolean);

  // v5.66 — the plan's photo for the ordering dialog, looked up by id in
  // the list that carries pictures (a handed-over object can be a slimmer projection).
  const planPhotoOf = (plan) => {
    const full = (Array.isArray(mealPlans) ? mealPlans : []).find((p) => p.id === plan?.id) || plan || {};
    return (Array.isArray(full.images) && full.images[0]) || full.image_url || null;
  };
  const deliveryCheckerPlacement = brand.delivery_checker_placement || 'above_plans';
  // Pass 3 - when the hero carries the checker pill, the standalone section
  // would ask the same question twice on one page; it renders only when
  // the pill is off (Design Studio) or the hero is hidden.
  const heroHasChecker = show.hero && brand.hero_show_checker !== false;
  const deliveryCheckerBlock = show.deliveryChecker && !heroHasChecker ? (
    <DeliveryCheckerSection
      copy={copy}
      primary={primary}
      subdomain={getSubdomain()}
      storeSlug={storeSlug}
      isPreview={isPreview}
    />
  ) : null;

  // v5.66 — the dish showcase strip: photographed menu items first (the
  // display feed, capped at 8); a plan-only store shows its photographed
  // plans instead. Prices come from the same helpers the cards use.
  const showcaseItems = (() => {
    const fromMenu = mealItems
      .filter((it) => it.image_url)
      .slice(0, 8)
      .map((it) => {
        const p = menuItemPrice(it);
        return {
          id: it.id, name: it.name, description: it.description, image_url: it.image_url,
          image_source: it.image_source,
          priceLabel: p > 0 ? `${menuHasSizes(it) ? 'from ' : ''}$${p.toFixed(2)}` : null,
        };
      });
    if (fromMenu.length) return fromMenu;
    return primaryMealPlans
      .map((plan) => {
        const img = (Array.isArray(plan.images) && plan.images[0]) || plan.image_url;
        const tier = (plan.pricing?.tiers || plan.tiers || [])[0] || {};
        const price = Number(tier.price || plan.price || 0);
        return img ? {
          id: plan.id, name: plan.name, description: plan.description, image_url: img,
          priceLabel: price > 0 ? `from $${price.toFixed(2)}` : null,
        } : null;
      })
      .filter(Boolean)
      .slice(0, 8);
  })();

  // Pass 3 — the hero's photos at PAGE scope: the header must know whether
  // it floats over a cover photo before the hero renders.
  const heroPhotos = Array.from(new Set([
    brand.hero_image_url,
    ...copy.galleryItems.map((g) => g.image_url),
    ...showcaseItems.map((it) => it.image_url),
  ].filter(Boolean))).slice(0, 4);
  const heroPhoto = brand.hero_image_url || (tokens.heroLayout !== 'centered' ? heroPhotos[0] : null) || null;
  const heroCover = show.hero && tokens.heroLayout === 'cover' && !!heroPhoto;
  const headerOverHero = heroCover && !headerSolid;

  const perCreditHint = (p) => {
    if (!p.credit_count || !p.price) return null;
    return `${p.currency || 'CAD'} $${(p.price / p.credit_count).toFixed(2)} / meal`;
  };

  const walletCreditsForProgram = (programId) =>
    wallet.find((row) => (row.meal_pass_program_id === programId) || (row.program?.id === programId))?.available_credits ?? null;

  // ── Storefront gate — render nothing until the tenant's storefront is
  //    positively enabled (the hook redirects to login when it isn't). ──
  if (gate.checking || !gate.allowed) {
    return (
      <div className="min-h-screen bg-neutral-950" data-testid="mealpass-store-gate" />
    );
  }

  // ── Loading skeleton ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950" data-testid="mealpass-store-loading">
        <div className="h-14 border-b border-white/5" />
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="h-96 bg-neutral-900 rounded-3xl animate-pulse mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-72 bg-neutral-900 rounded-2xl animate-pulse" />
            <div className="h-72 bg-neutral-900 rounded-2xl animate-pulse" />
            <div className="h-72 bg-neutral-900 rounded-2xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const faqs = Array.isArray(brand.faqs) && brand.faqs.length > 0
    ? brand.faqs
    : [
        { q: 'When do these credits expire?', a: 'Never. Once you buy a pass, the credits are yours forever — dine in, take out, or delivery whenever you like.' },
        { q: 'Can I redeem for anything on the menu?', a: 'Each pass lists what it covers. A few premium items may carry a small upgrade — the restaurant sets this.' },
        { q: 'What if I move away or change my mind?', a: 'Credits are honored directly by the restaurant. Refund and cancellation terms follow the restaurant\'s posted policy.' },
        { q: 'Where can I redeem?', a: 'Dine-in, pickup, or delivery — check each pass card for the fulfillment types it supports.' },
      ];

  const testimonials = [
    { name: 'Priya S.', quote: 'I skip the app fees and support a place I love. Best decision.' },
    { name: 'Marco D.', quote: 'Bought a 10-pack. Two months in and still not close to using them all — no rush, no stress.' },
    { name: 'Emily R.', quote: 'The convenience of just walking in and having lunch covered. Feels like a members-only club.' },
  ];

  return (
    <div
      className="min-h-screen sf-root"
      style={tokensToCssVars(tokens)}
      data-testid="mealpass-store"
    >
      {/* SEO head layer (v4.25) — client-injected title/description/OG/JSON-LD.
          Google renders JS and sees these; social scrapers do not (CRA has no
          SSR) — the /api/storefront/{subdomain}/seo.json feed backstops agents. */}
      <StorefrontSEO
        brand={brand}
        subdomain={getSubdomain()}
        pageType="home"
        programs={programs}
        mealPlans={mealPlans}
        cateringPackages={cateringPackages}
        faqs={faqs}
        isConceptPreview={tenantMeta?.is_concept_preview === true}
      />

      {/* Private Concept Preview banner (v4.25.1) — REQUIRED on every
          generated-but-unclaimed launch-engine store (safe-marketing §8.2):
          the visitor must understand this is a concept, not the restaurant's
          official site. Cleared automatically once the owner claims it. */}
      {tenantMeta?.is_concept_preview === true && (
        <div
          data-testid="storefront-concept-preview-banner"
          className="sticky top-0 z-50 bg-amber-400 text-amber-950 text-center px-4 py-2 text-xs sm:text-[13px] leading-snug font-medium"
        >
          <span className="font-bold uppercase tracking-wide mr-2">Private concept preview</span>
          This is a private concept preview created from publicly available
          information. It is not live, searchable, connected to payments, or
          affiliated with the restaurant until an authorized owner approves it.
        </div>
      )}

      {/* Preview-only badge: shown when demo fixtures were substituted for a
          gated/empty catalog so the designer knows what's sample vs real. */}
      {isPreview && demoDataActive && (
        <div
          data-testid="storefront-preview-demo-badge"
          className="fixed top-24 left-4 z-50 pointer-events-none select-none rounded-full bg-black/60 text-white/90 backdrop-blur-sm px-3 py-1.5 text-[11px] leading-none shadow-lg"
        >
          Preview — sample data shown where your catalog is empty
        </div>
      )}

      {/* ─── Announcement marquee ─────────────────────────────────────── */}
      {show.marquee && (
        <AnnouncementMarquee
          items={copy.marqueeItems}
          bg={tokens.primary}
          color={tokens.onPrimary}
        />
      )}

      {/* ─── Sticky header ────────────────────────────────────────────── */}
      {/* v5.66 — the header sits ON the page (paper, hairline) instead of
          floating over the hero: the split hero puts the photo beside the
          copy, so there is no dark band for a white header to read on. */}
      <header
        ref={(el) => {
          // Pass 3 - a CALLBACK ref: the header mounts only after the gate
          // resolves, and the mount-time measure effect had already run
          // against a null ref (headerH stayed 0, so the cover hero never
          // slid under the header).
          headerRef.current = el;
          if (el && el.offsetHeight) setHeaderH(el.offsetHeight);
        }}
        className={`sf-header transition-shadow ${headerOverHero ? 'sf-header-overlay sf-on-dark' : ''}`}
        style={{ boxShadow: headerSolid ? '0 6px 24px -20px rgba(20,14,10,0.35)' : 'none' }}
        data-testid="mealpass-store-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-[72px] flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 min-w-0"
            aria-label={brand.brand_name || 'Home'}
          >
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="h-10 w-10 rounded-full grid place-items-center flex-shrink-0"
                style={{ background: 'var(--sf-primary)', color: 'var(--sf-on-primary)' }}
              >
                <span className="text-base" style={{ fontFamily: headingFontStack, fontWeight: 700 }}>
                  {(brand.brand_name || 'M').charAt(0)}
                </span>
              </div>
            )}
            <span
              className="text-[17px] md:text-lg truncate max-w-[38vw] sm:max-w-none"
              style={{ fontFamily: headingFontStack, fontWeight: 700, color: headerOverHero ? '#ffffff' : 'var(--sf-ink)', letterSpacing: '-0.01em' }}
            >
              {brand.brand_name}
            </span>
          </button>

          {/* Pass 2 — once the visitor has scrolled past the hero the delivery
              checker pill rides in the header (wide screens), so the question
              "do you deliver to me?" is never more than a glance away. */}
          {pastHero && show.deliveryChecker && brand.hero_show_checker !== false && (
            <div className="hidden lg:block w-[380px] xl:w-[440px]">
              <HeroDeliveryChecker
                compact
                copy={copy}
                subdomain={getSubdomain()}
                storeSlug={storeSlug}
                isPreview={isPreview}
                state={checkerState}
                setState={setCheckerState}
                onOrder={orderableItemCount > 0 ? () => goOrderOnline() : () => scrollTo(primaryOfferAnchor())}
              />
            </div>
          )}

          {/* Nav row (v4.24): section jump-links + About/Contact pages. Only
              renders links for enabled sections, so it mirrors what's sold. */}
          {navItems.length > 0 && (
            <nav
              className="flex items-center gap-1"
              data-testid="mealpass-store-nav"
              aria-label="Storefront sections"
            >
              {navItems.filter((item) => !item.primary).map((item) => {
                // v5.12 — highlight the section the visitor is actually in.
                const isActive = activeSection && item.key === activeSection;
                return (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onClick}
                  aria-current={isActive ? 'true' : undefined}
                  data-active={isActive ? 'true' : undefined}
                  data-testid={item.testid || `mealpass-store-nav-${item.key}`}
                  className="hidden md:inline-flex sf-nav-link"
                >
                  {item.label}
                </button>
                );
              })}

              {/* Cart pill — appears whenever there are items in the cart. */}
              {cartItems && cartItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    navigate(checkoutDest());
                  }}
                  data-testid="mealpass-store-cart-pill"
                  className="sf-chip ml-1"
                  style={{ borderColor: 'var(--sf-ink)' }}
                >
                  <ShoppingBag className="w-3.5 h-3.5" aria-hidden="true" /> {cartItems.length}
                </button>
              )}

              {/* The primary "Order Now" CTA — a filled pill, last in the row. */}
              {navItems.filter((item) => item.primary).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onClick}
                  data-testid={`mealpass-store-nav-${item.key}`}
                  className="sf-btn sf-btn-primary ml-1 !py-2.5 !px-4 text-[13px] md:text-sm"
                >
                  {item.label}
                </button>
              ))}

              {/* Pass 2 — phones get a hamburger that opens a full-screen
                  menu sheet (the old dropdown squeezed the brand name out). */}
              {navItems.some((item) => !item.primary) && (
                <span className="md:hidden">
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(true)}
                    data-testid="mealpass-store-nav-more"
                    aria-label="Menu"
                    aria-expanded={mobileMenuOpen ? 'true' : 'false'}
                    className="sf-icon-btn ml-1"
                  >
                    <MenuIcon className="w-5 h-5" />
                  </button>
                </span>
              )}
            </nav>
          )}
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="sf-menu-sheet md:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-testid="mealpass-store-menu-sheet">
          <div className="flex items-center justify-between px-5 h-16 border-b" style={{ borderColor: 'var(--sf-line)' }}>
            <span className="text-lg truncate" style={{ fontFamily: headingFontStack, fontWeight: 700 }}>{brand.brand_name}</span>
            <button type="button" onClick={() => setMobileMenuOpen(false)} className="sf-icon-btn" aria-label="Close menu" data-testid="mealpass-store-menu-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 pt-1">
            {navItems.filter((item) => !item.primary).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => { setMobileMenuOpen(false); item.onClick(); }}
                data-testid={`mealpass-store-more-${item.key}`}
                className="sf-menu-link"
              >
                {item.label}
                <ArrowRight className="w-5 h-5 opacity-50" aria-hidden="true" />
              </button>
            ))}
          </div>
          <div className="px-5 pb-8 pt-3">
            {navItems.filter((item) => item.primary).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => { setMobileMenuOpen(false); item.onClick(); }}
                className="sf-btn sf-btn-primary w-full !py-4"
              >
                {item.label}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Hero (v5.66: split editorial) ────────────────────────────── */}
      {/* The copy reads as INK ON PAPER beside the photo (the old cinematic
          hero painted white text over the page colour whenever a store had
          no hero image — illegible on the founder's own demo). A merchant
          can still choose the centered/over-photo layout in Design Studio;
          that variant renders paper-on-dark ONLY when a photo exists. */}
      {show.hero && (() => {
        const heroOnDark = tokens.heroLayout === 'centered' && !!brand.hero_image_url;
        const heroPoints = Array.isArray(brand.hero_points)
          ? brand.hero_points.map((p) => String(p || '').trim()).filter(Boolean).slice(0, 3)
          : [];
        const lead = showcaseItems[0] || null;
        const heroCollage = !heroOnDark && !heroCover && tokens.heroLayout === 'collage' && heroPhotos.length >= 3;
        const heroSplit = !heroOnDark && !heroCover && !heroCollage && !!heroPhoto;
        const heroHasMedia = heroCollage || heroSplit;
        const heroDark = heroOnDark || heroCover;
        const showChecker = show.deliveryChecker && brand.hero_show_checker !== false;
        const onOrder = orderableItemCount > 0 ? () => goOrderOnline() : () => scrollTo(primaryOfferAnchor());
        const mutedOnHero = heroDark ? 'rgba(255,255,255,0.86)' : 'var(--sf-ink-muted)';
        return (
      <>
      <section
        ref={heroRef}
        className={`relative overflow-hidden ${heroCover ? 'sf-hero-cover sf-on-dark' : heroOnDark ? 'sf-on-dark' : 'sf-tint'}`}
        data-testid="mealpass-store-hero"
        style={{
          backgroundColor: heroOnDark ? tokens.dark : undefined,
          color: heroOnDark ? tokens.onDark : undefined,
          marginTop: heroCover ? -headerH : undefined,
          ...(getSectionStyle('hero')),
        }}
      >
        {heroCover && (
          <img src={heroPhoto} alt="" className="sf-hero-photo" data-testid="mealpass-store-hero-cover" />
        )}
        {heroOnDark && (
          <motion.div style={{ y: heroBgY }} className="absolute inset-0 -top-[10%] -bottom-[10%] z-0">
            <div
              className="absolute inset-0"
              style={{ backgroundImage: `url(${brand.hero_image_url})`, backgroundPosition: 'center', backgroundSize: 'cover' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          </motion.div>
        )}

        <div
          className={`sf-hero-inner relative z-10 sf-container ${
            heroCover
              ? 'min-h-[78svh] md:min-h-[88svh] flex flex-col justify-end pb-12 md:pb-20'
              : heroOnDark
                ? 'min-h-[calc(100svh-64px)] py-24 md:py-36 flex flex-col items-center justify-center text-center'
                : `pt-10 pb-14 md:pt-12 md:pb-20 ${
                  heroHasMedia
                    ? 'md:min-h-[calc(100svh-72px)] grid gap-10 md:gap-10 lg:gap-16 md:grid-cols-12 items-center'
                    : 'text-center'
                }`
          }`}
          style={heroCover ? { paddingTop: headerH + 40 } : undefined}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className={heroCover ? 'w-full max-w-2xl mx-auto md:mx-0 text-center md:text-left' : heroHasMedia ? 'md:col-span-6 lg:col-span-5' : 'max-w-3xl mx-auto'}
          >
            {brand.tagline && (
              <p
                className="sf-eyebrow"
                style={heroDark ? { color: 'rgba(255,255,255,0.78)' } : undefined}
                data-testid="mealpass-store-tagline"
              >
                {brand.tagline}
              </p>
            )}

            <h1
              className="sf-display"
              style={{ fontSize: heroCover ? 'clamp(2.5rem, 5.4vw, 4.6rem)' : 'clamp(2.75rem, 6.2vw, 5.5rem)', fontWeight: 600 }}
              data-testid="mealpass-store-hero-title"
            >
              {copy.heroTitle}{' '}
              <span className={phraseCls} data-testid="mealpass-store-hero-italic">
                {copy.heroItalic}
              </span>
            </h1>

            {copy.heroSubtitle && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.7 }}
                className={`mt-4 text-[17px] md:text-lg leading-relaxed ${heroCover ? 'hidden md:block max-w-xl mx-auto md:mx-0' : heroHasMedia ? 'max-w-xl' : 'max-w-2xl mx-auto'}`}
                style={{ fontFamily: bodyFontStack, color: mutedOnHero }}
              >
                {copy.heroSubtitle}
              </motion.p>
            )}

            {/* The merchant's own offer line (Design Studio → Hero). Blank = nothing. */}
            {brand.hero_offer_text && (
              <p
                className="mt-5 text-sm font-semibold"
                style={{ color: heroDark ? 'var(--sf-accent)' : 'var(--sf-primary)' }}
                data-testid="mealpass-store-hero-offer"
              >
                {brand.hero_offer_text}
              </p>
            )}

            {/* The delivery checker lives IN the hero. */}
            {showChecker && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.7 }}
                className={`${brand.hero_offer_text ? 'mt-3' : 'mt-6'} ${heroCover ? 'flex justify-center md:justify-start' : heroHasMedia ? '' : 'flex justify-center'}`}
              >
                <HeroDeliveryChecker
                  copy={copy}
                  subdomain={getSubdomain()}
                  storeSlug={storeSlug}
                  isPreview={isPreview}
                  onOrder={onOrder}
                  state={checkerState}
                  setState={setCheckerState}
                />
              </motion.div>
            )}

            {/* The two page CTAs: quiet links on the cover photo, buttons elsewhere. */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className={`${showChecker ? 'mt-5' : 'mt-8'} flex flex-wrap items-center ${heroCover ? 'gap-x-6 gap-y-2 justify-center md:justify-start' : `gap-3 ${heroHasMedia ? '' : 'justify-center'}`}`}
            >
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => scrollTo(primaryOfferAnchor())}
                data-testid="mealpass-store-hero-browse"
                className={heroCover ? 'sf-link inline-flex items-center gap-1.5' : `sf-btn ${
                  heroOnDark ? 'sf-btn-on-dark' : heroCTAStyle === 'outline' || showChecker ? 'sf-btn-outline' : 'sf-btn-primary'
                } ${heroCTAStyle === 'sharp' ? 'sf-btn-sharp' : ''}`}
              >
                {primaryOfferLabel()}
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => scrollTo(storyAnchor)}
                data-testid="mealpass-store-hero-how"
                className={heroCover ? 'sf-link opacity-85' : `sf-btn ${heroOnDark ? 'sf-btn-outline-dark' : 'sf-btn-ghost'} ${heroCTAStyle === 'sharp' ? 'sf-btn-sharp' : ''}`}
              >
                {copy.heroCTA2}
              </motion.button>
            </motion.div>

            {/* The merchant's own trust points (Design Studio → Hero). Nothing
                is invented here: an empty list renders nothing. On the cover
                hero they sit in the strip UNDER the photo. */}
            {!heroCover && heroPoints.length > 0 && (
              <ul
                className={`mt-8 flex flex-wrap gap-x-6 gap-y-2 ${heroHasMedia ? '' : 'justify-center'}`}
                data-testid="mealpass-store-hero-points"
              >
                {heroPoints.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium" style={{ color: heroOnDark ? 'rgba(250,246,240,0.8)' : 'var(--sf-ink-muted)' }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: heroOnDark ? 'var(--sf-accent)' : 'var(--sf-primary)' }} aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          {heroCollage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="order-first md:order-none md:col-span-6 lg:col-span-7 sf-collage md:h-[min(78vh,760px)]"
              data-testid="mealpass-store-hero-collage"
            >
              {heroPhotos.map((src, i) => (
                <div key={src} className={i > 0 ? 'aspect-[3/2] md:aspect-auto' : 'aspect-[3/4] md:aspect-auto'}>
                  <img src={src} alt="" loading={i > 0 ? 'lazy' : 'eager'} />
                </div>
              ))}
            </motion.div>
          )}

          {heroSplit && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="order-first md:order-none md:col-span-6 lg:col-span-7 relative"
              data-testid="mealpass-store-hero-photo"
            >
              <div className="sf-radius-lg overflow-hidden aspect-[4/3] md:aspect-auto md:h-[min(78vh,760px)] shadow-2xl">
                <img src={heroPhoto} alt="" className="w-full h-full object-cover" />
              </div>
              {lead && (
                <div
                  className="absolute bottom-4 left-4 md:-bottom-5 md:-left-8 sf-card p-3 pr-4 flex items-center gap-3 max-w-[270px]"
                  data-testid="mealpass-store-hero-dish-card"
                >
                  <img src={lead.image_url} alt="" className="h-14 w-14 rounded-xl object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight truncate" style={{ fontFamily: headingFontStack }}>{lead.name}</p>
                    {lead.priceLabel && <p className="text-xs mt-0.5" style={{ color: 'var(--sf-ink-muted)' }}>{lead.priceLabel}</p>}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* The trust strip under the cover photo: the merchant's own words, one quiet line. */}
      {heroCover && heroPoints.length > 0 && (
        <div className="sf-trust" data-testid="mealpass-store-hero-points">
          <ul>
            {heroPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
      </>
        );
      })()}

      {/* ─── Dish showcase strip (v5.66) ──────────────────────────────── */}
      {show.showcase && showcaseItems.length >= 3 && (
      <Section id="showcase" className="sf-section sf-surface-white" style={getSectionStyle('showcase')} data-testid="mealpass-store-showcase">
        <div className="sf-container">
          <div className="sf-section-head">
            <span className="sf-eyebrow">{copy.showcaseEyebrow}</span>
            <h2 className="sf-h2">
              {copy.showcaseHeadline}{' '}
              <span className={phraseCls}>{copy.showcaseHeadlineItalic}</span>
            </h2>
          </div>
          <div ref={showcaseStripRef} className="sf-strip -mx-5 px-5 sm:-mx-6 sm:px-6 md:mx-0 md:px-0" data-testid="mealpass-store-showcase-strip">
            {showcaseItems.map((it, i) => (
              <motion.article
                key={it.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: Math.min(i, 5) * 0.06 }}
                className="sf-dish-overlay w-[240px] sm:w-[270px] md:w-[300px]"
                data-testid={`mealpass-store-showcase-${it.id}`}
              >
                <img src={it.image_url} alt={it.name} loading="lazy" />
                <div className="sf-dish-txt">
                  <h3 className="text-[17px] font-semibold leading-tight" style={{ fontFamily: headingFontStack }}>{it.name}</h3>
                  {it.description && (
                    <p className="mt-1 text-[13px] leading-snug line-clamp-2 opacity-85">{it.description}</p>
                  )}
                  <div className="mt-2 flex items-center justify-center gap-2">
                    {it.priceLabel && <span className="text-[13px] font-semibold">{it.priceLabel}</span>}
                    {it.image_source === 'ai' && (
                      <span className="text-[10px] opacity-70" data-testid="storefront-ai-image-tag">AI generated image</span>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button type="button" onClick={() => scrollStrip(-1)} className="sf-icon-btn !w-10 !h-10" aria-label="Previous dishes">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button type="button" onClick={() => scrollStrip(1)} className="sf-icon-btn !w-10 !h-10" aria-label="More dishes">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          {show.menuSection && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => scrollTo(menuAnchor)}
                className="sf-btn sf-btn-primary"
                data-testid="mealpass-store-showcase-menu"
              >
                Explore the menu <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </Section>
      )}

      {/* ─── Delivery checker (placement: below hero) ─────────────────── */}
      {deliveryCheckerPlacement === 'below_hero' && deliveryCheckerBlock}

      {/* ─── How it works ─────────────────────────────────────────────── */}
      {show.howItWorks && (
      <Section className="sf-section sf-tint-accent" id="how-it-works" style={getSectionStyle('how-it-works')} data-testid="mealpass-store-how-it-works">
        <div className="sf-container">
          <div className="sf-section-head">
            <span className="sf-eyebrow">{copy.stepsEyebrow}</span>
            <h2 className="sf-h2">
              {copy.stepsHeadline}{' '}
              <span className={phraseCls}>{copy.stepsHeadlineItalic}</span>
            </h2>
          </div>

          <div className={`grid grid-cols-1 gap-5 md:gap-6 ${copy.steps.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            {copy.steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="sf-card p-7 md:p-8"
                data-testid={`mealpass-store-step-${i}`}
              >
                <div className="sf-step-num mb-6">{step.number || step.n || String(i + 1).padStart(2, '0')}</div>
                <h3
                  className="text-xl md:text-[1.35rem] leading-snug mb-2.5"
                  style={{ fontFamily: headingFontStack, fontWeight: 600 }}
                >
                  {step.title}
                </h3>
                <p className="leading-relaxed text-[15px]" style={{ color: 'var(--sf-ink-muted)' }}>{step.body}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 md:mt-12 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => scrollTo(primaryOfferAnchor())}
              className="sf-btn sf-btn-primary"
              data-testid="mealpass-store-steps-cta"
            >
              {primaryOfferLabel()}
              <ArrowRight className="w-4 h-4" />
            </button>
            {show.menuSection && (
              <button type="button" onClick={() => scrollTo(menuAnchor)} className="sf-link" style={{ color: 'var(--sf-ink)' }}>
                Explore the menu
              </button>
            )}
          </div>
        </div>
      </Section>
      )}

      {/* v5.12 — gallery lightbox (what the clickable-looking tiles now do) */}
      {lightboxIdx !== null && copy.galleryItems[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxIdx(null)}
          data-testid="mealpass-store-gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxIdx(null)}
            aria-label="Close photo"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
          >
            <X className="h-5 w-5" />
          </button>
          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {copy.galleryItems[lightboxIdx].image_url && (
              <img
                src={copy.galleryItems[lightboxIdx].image_url}
                alt={copy.galleryItems[lightboxIdx].caption || ''}
                className="w-full max-h-[80vh] object-contain rounded-xl"
              />
            )}
            {copy.galleryItems[lightboxIdx].caption && (
              <figcaption className="mt-4 text-center text-white/80 text-sm md:text-base">
                {copy.galleryItems[lightboxIdx].caption}
              </figcaption>
            )}
          </figure>
        </div>
      )}

      {/* v5.13.1 — persistent LIVE order-status chip (Honest Greens "hang
          tight" pattern): a customer who placed an order and came back to the
          website always sees where it stands; tap → the tracking screen. */}
      {activeOrder && (
        <button
          type="button"
          onClick={() => {
            const sub = getSubdomain();
            const slug = activeOrder.entry.slug || storeSlug;
            navigate(`/order?tenant=${encodeURIComponent(sub)}`
              + (slug ? `&store_slug=${encodeURIComponent(slug)}` : '')
              + `&track=${encodeURIComponent(activeOrder.entry.token)}`);
          }}
          className={`fixed inset-x-4 z-[61] mx-auto max-w-3xl rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 text-white text-sm ${menuCartCount > 0 ? 'bottom-24' : 'bottom-4'}`}
          style={{ background: '#1a1613' }}
          data-testid="mealpass-store-active-order-chip"
        >
          <span className="h-2 w-2 rounded-full animate-pulse flex-shrink-0" style={{ background: primary }} aria-hidden="true" />
          <span className="flex-1 min-w-0 text-left font-semibold truncate">
            Order {activeOrder.live.order_number != null ? `#${activeOrder.live.order_number}` : ''} — {
              activeOrder.live.prep_state === 'ready' ? 'ready for pickup!'
              : activeOrder.live.status === 'confirmed' ? 'being prepared'
              : activeOrder.live.status === 'pending_payment' ? 'awaiting payment'
              : 'placed'}
          </span>
          <span className="text-white/60 text-xs flex-shrink-0">View status →</span>
        </button>
      )}

      {/* v5.12 — storefront menu cart bar. Totals shown here are indicative;
          the /order page re-prices server-side (tax + store currency + the
          connected-account charge) — it stays the single money authority. */}
      {menuCartCount > 0 && (
        <div
          className="fixed bottom-0 inset-x-0 z-[60] pl-4 pr-20 sm:pr-24 pb-4 pointer-events-none"
          data-testid="mealpass-store-cart-bar"
        >
          <div
            className="pointer-events-auto max-w-3xl mx-auto rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 text-white"
            style={{ background: '#1a1613' }}
          >
            <ShoppingBag className="h-5 w-5 flex-shrink-0" style={{ color: primary }} aria-hidden="true" />
            <div className="flex-1 min-w-0 text-sm">
              <span className="font-semibold" data-testid="mealpass-store-cart-count">
                {menuCartCount} {menuCartCount > 1 ? 'items' : 'item'}
              </span>
              <span className="text-white/60"> · ${menuCartSubtotal.toFixed(2)}</span>
            </div>
            <button
              type="button"
              onClick={() => setMenuCart({})}
              className="text-xs text-white/60 hover:text-white underline underline-offset-2"
              data-testid="mealpass-store-cart-clear"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => goOrderOnline(menuCart)}
              data-testid="mealpass-store-cart-checkout"
              className="rounded-full px-5 py-2 text-sm font-semibold text-white flex-shrink-0"
              style={{ background: primary }}
            >
              Checkout
            </button>
          </div>
        </div>
      )}

      {/* ─── Programs (Choose your pass) ──────────────────────────────── */}
      {show.mealpassSection && (
      <>
      <div ref={programsAnchor} />
      <Section
        className="sf-section sf-dark-slab sf-on-dark sf-slab"
        id="passes"
        style={getSectionStyle('passes')}
        data-testid="mealpass-store-programs"
      >
        <div className="sf-container relative">
          <div className="sf-section-head">
            <SectionLabel color={accent} testid="mealpass-store-section-label-passes">{copy.programsEyebrow}</SectionLabel>
            <h2 className="sf-h2">
              {copy.programsHeadline}{' '}
              <span className={phraseCls}>{copy.programsHeadlineItalic}</span>
            </h2>
            <p>{copy.programsSubheadline}</p>
          </div>

          {error && (
            <div className="max-w-lg mx-auto mb-8 p-3 rounded-lg bg-red-900/40 text-red-100 border border-red-800/50 text-center text-sm" data-testid="mealpass-store-error">
              {error}
            </div>
          )}

          {!programs.length ? (
            <div className="max-w-xl mx-auto text-center py-12" data-testid="mealpass-store-empty">
              <Ticket className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <h3
                className="text-2xl mb-2"
                style={{ fontFamily: headingFontStack, fontWeight: 500 }}
              >
                Coming soon
              </h3>
              <p className="text-neutral-400">
                {brand.brand_name} hasn't published any meal passes yet. Check back soon.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
              {programs.map((program, idx) => {
                const allowed = Array.isArray(program.allowed_fulfillment) ? program.allowed_fulfillment : [];
                const dineIn = allowed.includes('dine_in');
                const pickup = allowed.includes('pickup');
                const delivery = allowed.includes('delivery');
                const owned = walletCreditsForProgram(program.id);
                const perCredit = perCreditHint(program);
                const isFeatured = Number(idx) > 0 && Number(idx) < 2 && programs.length >= 3;

                return (
                  <motion.article
                    key={program.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: idx * 0.12, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -6 }}
                    className={`relative sf-radius overflow-hidden flex flex-col ${
                      isFeatured ? 'md:-mt-4 md:mb-4 shadow-2xl' : 'sf-card-dark shadow-lg'
                    }`}
                    style={{
                      background: isFeatured ? primary : undefined,
                      color: isFeatured ? tokens.onPrimary : tokens.onDark,
                    }}
                    data-testid={`mealpass-store-card-${program.id}`}
                  >
                    {isFeatured && (
                      <div
                        className="absolute top-4 right-4 z-10 text-[9px] tracking-[0.3em] uppercase font-semibold px-2 py-1 rounded-full"
                        style={{ background: accent, color: '#1a1613' }}
                      >
                        Most Popular
                      </div>
                    )}

                    {/* Optional cover image — shown when merchant uploads one */}
                    {program.image_url && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={program.image_url}
                          alt={program.public_name || program.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}

                    <div className="p-8 flex-1 flex flex-col">
                      <div className="mb-6">
                        <h3
                          className="text-2xl leading-tight mb-1"
                          style={{ fontFamily: headingFontStack, fontWeight: 500 }}
                        >
                          {program.public_name || program.name}
                        </h3>
                        {program.savings_label && (
                          <p
                            style={{ fontFamily: scriptFontStack, color: isFeatured ? '#fff' : accent, fontSize: '1.5rem' }}
                          >
                            {program.savings_label}
                          </p>
                        )}
                      </div>

                      <div className="mb-6">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span
                            className="text-6xl leading-none"
                            style={{ fontFamily: headingFontStack, fontWeight: 500 }}
                          >
                            {program.credit_count}
                          </span>
                          <span className="text-sm opacity-70">credits</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-semibold">
                            {program.currency || 'CAD'} ${program.price?.toFixed(2)}
                          </span>
                          {perCredit && (
                            <span className="text-xs opacity-60">· {perCredit}</span>
                          )}
                        </div>
                      </div>

                      {program.description && (
                        <p className="text-sm opacity-80 leading-relaxed mb-5 line-clamp-4">
                          {program.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-1.5 mb-6">
                        {dineIn && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/20">
                            <Utensils className="w-3 h-3" /> Dine-in
                          </span>
                        )}
                        {pickup && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/20">
                            <Store className="w-3 h-3" /> Pickup
                          </span>
                        )}
                        {delivery && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/20">
                            <Truck className="w-3 h-3" /> Delivery
                          </span>
                        )}
                      </div>

                      {owned !== null && owned > 0 && (
                        <div
                          className="mb-4 text-xs px-3 py-2 rounded-md bg-white/10"
                          data-testid={`mealpass-store-owned-${program.id}`}
                        >
                          You already own {owned} {Number(owned) > 1 ? 'credits' : 'credit'}
                        </div>
                      )}

                      <div className="mt-auto pt-2">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleBuy(program)}
                          data-testid={`mealpass-store-buy-${program.id}`}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm tracking-[0.2em] uppercase font-medium transition-colors"
                          style={{
                            background: isFeatured ? '#fff' : accent,
                            color: isFeatured ? primary : '#1a1613',
                          }}
                        >
                          {(owned !== null && owned > 0) ? 'Add more' : 'Buy this pass'}
                          <ArrowRight className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </Section>
      </>
      )}

      {/* ─── Delivery checker (placement: above plans, the default) ───── */}
      {deliveryCheckerPlacement === 'above_plans' && deliveryCheckerBlock}

      {/* ─── Meal Plans / Subscriptions section ───────────────────────── */}
      {/* Renders when merchant has enabled meal plans AND has at least one active plan.
          "Order now" links to the existing storefront checkout so we don't break the flow. */}
      {show.mealplansSection && (
      <div ref={mealPlansAnchor}>
      <Section className="sf-section" id="plans" style={getSectionStyle('plans')} data-testid="mealpass-store-meal-plans">
        <div className="sf-container">
          <div className="sf-section-head">
            <SectionLabel color={primary} testid="mealpass-store-section-label-plans">{copy.plansEyebrow}</SectionLabel>
            <h2 className="sf-h2">
              {copy.plansHeadline}{' '}
              <span className={phraseCls}>{copy.plansHeadlineItalic}</span>
            </h2>
            {copy.plansSubheadline && <p>{copy.plansSubheadline}</p>}
          </div>

          {/* Tag filter chips — high-contrast, tucked directly under the heading */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="mealpass-store-tag-filters">
              <button
                type="button"
                onClick={() => setSelectedTag(null)}
                data-testid="mealpass-store-tag-all"
                className="sf-chip"
                data-active={!selectedTag ? 'true' : undefined}
              >
                All plans
              </button>
              {tags.map((t) => {
                const isActive = selectedTag && (selectedTag.id === t.id || selectedTag.name === t.name);
                return (
                  <button
                    key={t.id || t.name}
                    type="button"
                    onClick={() => setSelectedTag(t)}
                    data-testid={`mealpass-store-tag-${t.id || t.name}`}
                    className="sf-chip"
                    data-active={isActive ? 'true' : undefined}
                  >
                    {t.icon && <span className="text-sm" style={{ filter: isActive ? 'none' : 'grayscale(0)' }}>{t.icon}</span>}
                    {t.display_name || t.name}
                    {typeof t.meal_plan_count === 'number' && t.meal_plan_count > 0 && (
                      <span
                        className="text-[10px] normal-case tracking-normal font-normal"
                        style={{ opacity: isActive ? 0.85 : 0.55 }}
                      >
                        ({t.meal_plan_count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {primaryMealPlans
              .filter((plan) => {
                if (!selectedTag) return true;
                // Tags carry `meal_plan_ids` (list of plan.id) — canonical shape from
                // /api/storefront/tags. Also defensively support plans that have
                // tags[] or tag_ids[] embedded (some legacy shapes).
                const tagPlanIds = Array.isArray(selectedTag.meal_plan_ids) ? selectedTag.meal_plan_ids : [];
                if (tagPlanIds.includes(plan.id)) return true;
                const planTags = plan.tags || [];
                const planTagIds = plan.tag_ids || [];
                return (
                  (Array.isArray(planTags) && planTags.some((t) => (typeof t === 'string' ? t === selectedTag.name : t?.name === selectedTag.name || t?.id === selectedTag.id))) ||
                  (Array.isArray(planTagIds) && planTagIds.includes(selectedTag.id))
                );
              })
              .slice(0, 6).map((plan, idx) => {
              // Pull the first tier for the headline price. Meal plans use pricing.tiers.
              const tiers = plan.pricing?.tiers || plan.tiers || [];
              const firstTier = tiers[0] || {};
              const tierQty = firstTier.quantity || firstTier.meals || plan.meals_per_week || null;
              const tierPrice = firstTier.price || plan.price || null;
              const checkoutPath = checkoutDest(`meal_plan=${plan.id}`);
              return (
                <motion.article
                  key={plan.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.65, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -5 }}
                  className="relative overflow-hidden sf-card flex flex-col"
                  data-testid={`mealpass-store-meal-plan-${plan.id}`}
                >
                  {/* Plan image if present — meal plans store photos in `images: [url, ...]`
                      (canonical shape used by legacy CustomerStorefront + admin uploads).
                      Falls back to `image_url` for any future single-image shape. */}
                  {(() => {
                    const img = (Array.isArray(plan.images) && plan.images[0]) || plan.image_url;
                    return img ? (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={img} alt={plan.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
                      </div>
                    ) : null;
                  })()}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3
                        className="text-xl leading-tight"
                        style={{ fontFamily: headingFontStack, fontWeight: 600 }}
                      >
                        {plan.name}
                      </h3>
                      {tierQty !== null && (
                        <span className="sf-chip sf-chip-soft shrink-0 !py-1 !px-2.5 !text-[11px]">{tierQty} meals / wk</span>
                      )}
                    </div>
                    {plan.description && (
                      <p className="text-[15px] leading-relaxed mb-4 line-clamp-3" style={{ color: 'var(--sf-ink-muted)' }}>
                        {plan.description}
                      </p>
                    )}
                    <div className="mb-5 flex items-baseline gap-2">
                      {tierPrice !== null && (
                        <>
                          <span className="text-2xl font-semibold" style={{ fontFamily: headingFontStack }}>
                            ${Number(tierPrice).toFixed(2)}
                          </span>
                          <span className="text-sm" style={{ color: 'var(--sf-ink-muted)' }}>
                            {tiers.length > 1 ? 'starting' : (tierQty !== null ? `for ${tierQty} meals` : '')}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-auto pt-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setPreAddCartCount(cartItems?.length || 0);
                          setSelectedMealPlan(plan);
                        }}
                        data-testid={`mealpass-store-meal-plan-order-${plan.id}`}
                        className="sf-btn sf-btn-primary w-full"
                      >
                        Order this plan
                        <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* "View all plans →" link — shown only when there's more than what fits above */}
          {primaryMealPlans.length > 6 && (
            <div className="mt-10 text-center" data-testid="mealpass-store-view-all-plans">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => {
                  const dest = storeSlug ? `/customers/${storeSlug}/plans` : '/customers/plans';
                  navigate(dest);
                }}
                className="sf-btn sf-btn-outline"
              >
                View all {primaryMealPlans.length} plans
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <p className="mt-3 text-xs" style={{ color: 'var(--sf-ink-muted)' }}>
                Browse the full catalogue with search, filters, and sorting.
              </p>
            </div>
          )}
        </div>
      </Section>
      </div>
      )}

      {/* ─── Catering section (v4.24) ─────────────────────────────────── */}
      {/* Opt-in (brand.catering_enabled === true) + at least one active
          package. Cards open the CateringOrderModal (configure event →
          upsells → cart → checkout). */}
      {show.cateringSection && (
      <div ref={cateringAnchor}>
      <Section className="sf-section" id="catering" style={getSectionStyle('catering')} data-testid="mealpass-store-catering">
        <div className="sf-container">
          <div className="sf-section-head">
            <SectionLabel color={primary} testid="mealpass-store-section-label-catering">{copy.cateringEyebrow}</SectionLabel>
            <h2 className="sf-h2">
              {copy.cateringHeadline}{' '}
              <span className={phraseCls}>{copy.cateringHeadlineItalic}</span>
            </h2>
            <p>{copy.cateringSubheadline}</p>
            {cateringAvailability?.min_lead_hours > 0 && (
              <p className="mt-3 text-xs tracking-[0.15em] uppercase text-neutral-400">
                Order at least {cateringAvailability.min_lead_hours} hours ahead
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {cateringPackages.slice(0, 6).map((pkg, idx) => (
              <motion.article
                key={pkg.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.65, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -5 }}
                className="relative overflow-hidden sf-card flex flex-col"
                data-testid={`mealpass-store-catering-${pkg.id}`}
              >
                {pkg.image_url && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={pkg.image_url} alt={pkg.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3
                      className="text-xl leading-tight"
                      style={{ fontFamily: headingFontStack, fontWeight: 500 }}
                    >
                      {pkg.name}
                    </h3>
                    <span className="shrink-0 text-[10px] tracking-[0.15em] uppercase font-semibold px-2.5 py-1 rounded-full border border-neutral-200 text-neutral-500">
                      {pkg.pricing_model === 'tray' ? 'By the tray' : 'Per person'}
                    </span>
                  </div>
                  {pkg.description && (
                    <p className="text-sm text-neutral-600 leading-relaxed mb-4 line-clamp-3">
                      {pkg.description}
                    </p>
                  )}
                  <div className="mb-4">
                    {pkg.pricing_model === 'tray' ? (
                      (() => {
                        const prices = (pkg.tray_items || []).flatMap((t) => (t.sizes || []).map((s) => Number(s.price) || 0)).filter((p) => p > 0);
                        const from = prices.length ? Math.min(...prices) : null;
                        return from !== null ? (
                          <div className="text-lg font-semibold" style={{ color: primary }}>
                            Trays from ${from.toFixed(2)}
                          </div>
                        ) : null;
                      })()
                    ) : (
                      <div className="text-lg font-semibold" style={{ color: primary }}>
                        ${Number(pkg.price_per_person || 0).toFixed(2)}
                        <span className="ml-1 text-xs text-neutral-500 font-normal">/ person</span>
                      </div>
                    )}
                    <div className="text-xs text-neutral-500 mt-1">
                      Minimum {pkg.min_persons} {Number(pkg.min_persons) > 1 ? 'guests' : 'guest'}
                      {(pkg.slots || []).length > 0 && (
                        <> · {(pkg.slots || []).map((s) => `${s.choose} ${s.name}`).join(' · ')}</>
                      )}
                    </div>
                  </div>
                  <div className="mt-auto pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedCateringPackage(pkg)}
                      data-testid={`mealpass-store-catering-order-${pkg.id}`}
                      className="sf-btn sf-btn-outline w-full"
                    >
                      Plan your event
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </Section>
      </div>
      )}

      {/* ─── Menu (à-la-carte items) ──────────────────────────────────── */}
      {show.menuSection && (
      <div ref={menuAnchor}>
      <Section
        className="sf-section sf-tint"
        id="menu"
        style={getSectionStyle('menu')}
        data-testid="mealpass-store-menu"
      >
        <div className="sf-container">
          <div className="sf-section-head">
            <SectionLabel color={primary} testid="mealpass-store-section-label-menu">À la carte</SectionLabel>
            <h2 className="sf-h2">
              Our{' '}
              <span className={phraseCls}>menu.</span>
            </h2>
            <p>Individual dishes ready to order. Perfect for a one-off meal.</p>
          </div>

          {/* Menu grouping — merchant selects date-based (per-day sections) or
              category-based (grouped by category, the default). */}
          {(() => {
            const isDateMode = brand.menu_mode === 'date';
            // Common renderer for a menu item card — works for both meal_items and
            // menu_calendar_entries (both shapes carry name/description/price/image_url).
            // `orderable` is only true for the category-based menu (real
            // meal_items). Date-mode rows are calendar entries, not the
            // /order feed's items, so they stay read-only.
            const renderItem = (item, idx, orderable = false) => {
              // v5.12 (codex) — the effective price is the first NON-ZERO of
              // price → base_price → cheapest tier (a tier-priced dish has
              // base_price 0 but IS orderable), used for BOTH the displayed
              // price and the Add-to-cart gate so the two can't disagree.
              const effPrice = menuItemPrice(item);
              const price = effPrice > 0
                ? effPrice
                : (item.price ?? item.base_price ?? (item.price_tiers?.[0]?.price) ?? null);
              const canOrder = orderable && effPrice > 0 && orderableIds.has(item.id);
              const qty = menuCart[item.id] || 0;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: (idx % 3) * 0.06 }}
                  className="group flex items-start gap-4 pb-5 border-b border-neutral-200"
                  data-testid={`mealpass-store-menu-item-${item.id}`}
                >
                  {item.image_url && (
                    <div className="flex-shrink-0 overflow-hidden rounded-xl">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-20 h-20 md:w-24 md:h-24 object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      {item.image_source === 'ai' && (
                        <p className="text-[8px] text-neutral-400 text-center mt-0.5 leading-none"
                           data-testid="storefront-ai-image-tag">AI generated image</p>
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <h4
                        className="text-lg leading-tight"
                        style={{ fontFamily: headingFontStack, fontWeight: 500 }}
                      >
                        {item.name}
                      </h4>
                      {price !== null && price !== undefined && (
                        <span
                          className="text-base flex-shrink-0"
                          style={{ fontFamily: headingFontStack, fontStyle: 'italic', color: primary }}
                        >
                          {menuHasSizes(item) ? 'from ' : ''}${Number(price).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-neutral-600 leading-snug line-clamp-2">
                        {item.description}
                      </p>
                    )}
                    {item.variant && (
                      <p className="text-xs text-neutral-500 mt-1 italic">
                        {item.variant}
                      </p>
                    )}
                    {canOrder && (
                      <div className="mt-2">
                        {!qty ? (
                          <button
                            type="button"
                            onClick={() => (menuHasSizes(item)
                              ? openSizePickerOnOrderPage(item)
                              : bumpMenuCart(item.id, 1))}
                            data-testid={menuHasSizes(item)
                              ? `mealpass-store-menu-choose-size-${item.id}`
                              : `mealpass-store-menu-add-${item.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition hover:text-white focus:outline-none focus-visible:ring-2"
                            style={{ borderColor: primary, color: primary }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = primary; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = primary; }}
                            aria-label={menuHasSizes(item)
                              ? `Choose a size for ${item.name}`
                              : `Add ${item.name} to cart`}
                          >
                            <Plus className="w-3 h-3" aria-hidden="true" />
                            {menuHasSizes(item) ? 'Choose size' : 'Add'}
                          </button>
                        ) : (
                          <div
                            className="inline-flex items-center gap-3 rounded-full px-2 py-1"
                            style={{ background: `${primary}14` }}
                            data-testid={`mealpass-store-menu-qty-${item.id}`}
                          >
                            <button
                              type="button"
                              onClick={() => bumpMenuCart(item.id, -1)}
                              className="p-1 rounded-full hover:bg-white/70"
                              aria-label={`Remove one ${item.name}`}
                            >
                              <Minus className="w-3 h-3" style={{ color: primary }} aria-hidden="true" />
                            </button>
                            <span className="text-xs font-semibold min-w-[1ch] text-center" style={{ color: primary }}>{qty}</span>
                            <button
                              type="button"
                              onClick={() => bumpMenuCart(item.id, 1)}
                              className="p-1 rounded-full hover:bg-white/70"
                              aria-label={`Add another ${item.name}`}
                            >
                              <Plus className="w-3 h-3" style={{ color: primary }} aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Not orderable because the KITCHEN said so (86'd, or
                        outside its hours) — say which, in the server's own
                        words. Silently hiding Add reads as a broken page. */}
                    {!canOrder && unavailableReasons[item.id] && (
                      <p className="mt-2 text-xs font-medium text-amber-700"
                         data-testid={`mealpass-store-menu-unavailable-${item.id}`}>
                        {unavailableReasons[item.id]}
                      </p>
                    )}
                  </div>
                </motion.div>
              );
            };

            if (isDateMode) {
              // Group menu_calendar entries by date, then render one section per day.
              const byDate = {};
              menuCalendar.forEach((e) => {
                const d = e.date || 'unscheduled';
                if (!byDate[d]) byDate[d] = [];
                byDate[d].push(e);
              });
              const dates = Object.keys(byDate).sort();
              const fmtDay = (isoDate) => {
                try {
                  const dt = new Date(isoDate + 'T00:00:00');
                  const day = dt.toLocaleDateString('en-US', { weekday: 'long' });
                  const md = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return { day, md };
                } catch (_e) { return { day: isoDate, md: '' }; }
              };
              return (
                <div className="space-y-14">
                  {dates.map((d) => {
                    const { day, md } = fmtDay(d);
                    return (
                      <div key={d} data-testid={`mealpass-store-menu-day-${d}`}>
                        <div className="text-center mb-6">
                          <div
                            className="text-xs tracking-[0.3em] uppercase mb-1"
                            style={{ color: primary, fontWeight: 600 }}
                          >
                            {md}
                          </div>
                          <h3
                            className="text-2xl md:text-3xl"
                            style={{ fontFamily: headingFontStack, fontWeight: 500 }}
                          >
                            {day}
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mx-auto">
                          {byDate[d].map((entry, idx) => renderItem(entry, idx))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Category-based (default): Impossible Foods-style tabbed product carousel.
            const q = menuSearchQuery.trim().toLowerCase();
            const filtered = q
              ? mealItems.filter((mi) => [mi.name, mi.description, mi.category].some(
                  (f) => String(f || '').toLowerCase().includes(q)))
              : mealItems;
            const grouped = {};
            filtered.forEach((mi) => {
              const cat = mi.category || 'Main dishes';
              if (!grouped[cat]) grouped[cat] = [];
              grouped[cat].push(mi);
            });
            // v5.25 — THE MERCHANT'S ORDER, and NOT a second copy of the rule.
            //
            // This used to alphabetise the grouped keys, destroying an order
            // the server had already produced. (The old expression is
            // deliberately not quoted here: a contract forbids it in this
            // file, and a pin that has to strip comments to avoid its own
            // example is one edit from breaking.) The feed
            // arrives sorted by `utils.menu_item_state.sort_items_for_display`
            // (category per the dragged layout, then sort_order, then name),
            // which is THE ONE display ordering the storefront, /order, the
            // layout manager and the KitchenHub push all share. Its own
            // docstring says why: "two private copies is how the paused-status
            // vocabulary drifted into four".
            //
            // So the categories are taken in ORDER OF FIRST APPEARANCE rather
            // than re-ranked here against `category_order`. Re-ranking would
            // have worked and would have been a third implementation of one
            // rule, free to drift from the other two. Built explicitly into an
            // array instead of relying on Object key order, because a category
            // named "5" is an integer-like key and JS would hoist it.
            const categories = [];
            filtered.forEach((mi) => {
              const cat = mi.category || 'Main dishes';
              if (!categories.includes(cat)) categories.push(cat);
            });
            const activeCat = menuActiveCategory || categories[0] || '';
            return (
              <div>
                {/* Search bar */}
                <div className="relative max-w-md mx-auto mb-6" data-testid="menu-search-bar">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                  <input
                    type="text"
                    value={menuSearchQuery}
                    onChange={(e) => { setMenuSearchQuery(e.target.value); setMenuActiveCategory(null); }}
                    placeholder="Search dishes…"
                    className="w-full pl-10 pr-4 py-2.5 rounded-full border border-neutral-200 bg-white text-sm focus:outline-none focus:ring-2 transition-shadow"
                    style={{ '--tw-ring-color': `${primary}40` }}
                    data-testid="menu-search-input"
                  />
                  {menuSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setMenuSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-neutral-100"
                      data-testid="menu-search-clear"
                    >
                      <X className="h-3.5 w-3.5 text-neutral-400" />
                    </button>
                  )}
                </div>

                {/* Category tabs — horizontal scrollable pill bar */}
                {categories.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-4 mb-8 -mx-1 px-1 snap-x no-scrollbar" data-testid="menu-category-tabs">
                    {categories.map((cat) => {
                      const isActive = cat === activeCat;
                      return (
                        <motion.button
                          key={cat}
                          type="button"
                          onClick={() => setMenuActiveCategory(cat)}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          className={`snap-start flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 border-2 ${
                            isActive
                              ? 'text-white border-transparent shadow-md'
                              : 'text-neutral-600 border-neutral-200 hover:border-neutral-400 bg-white'
                          }`}
                          style={isActive ? { background: primary, borderColor: primary } : undefined}
                          data-testid={`menu-tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {cat}
                          <span className={`ml-1.5 text-xs ${isActive ? 'text-white/70' : 'text-neutral-400'}`}>
                            {grouped[cat].length}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                )}

                {/* Grid layout for active category items */}
                <motion.div
                  key={activeCat}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mx-auto">
                    {(grouped[activeCat] || []).map((item, idx) => renderItem(item, idx, true))}
                  </div>
                </motion.div>

                {/* Show all categories fallback for small menus */}
                {categories.length <= 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mx-auto">
                    {mealItems.map((item, idx) => renderItem(item, idx, true))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* v5.8 — end-of-menu "Order Now" CTA so a customer can order online
              right after browsing the menu (same online /order entry as the header). */}
          {orderableItemCount > 0 && (
            <div className="mt-16 text-center" data-testid="mealpass-store-menu-order-cta">
              <button
                type="button"
                onClick={() => goOrderOnline()}
                data-testid="mealpass-store-menu-order-now"
                className="sf-btn sf-btn-primary"
              >
                {/* v5.21.19 (founder) — closed is no longer a dead label: the
                    order page can now SCHEDULE into the next open window, so
                    the button says so and still hands off. */}
                {(orderHours?.enabled && !orderHours.is_open)
                  ? (orderHours.opens_time ? `Schedule ahead — opens ${orderHours.opens_time}` : 'Schedule an order')
                  : 'Order Now'}
              </button>
              <p className="mt-3 text-xs text-neutral-500">
                {(orderHours?.enabled && !orderHours.is_open)
                  ? (orderHours.opens_time ? `Opens for orders at ${orderHours.opens_time}. Browse the menu anytime — or schedule an order for when we're open.` : 'Ordering is closed right now — browse the menu anytime, or schedule an order for when we\'re open.')
                  : 'Order online for pickup — pay by card or redeem a meal pass.'}
              </p>
            </div>
          )}
        </div>
      </Section>
      </div>
      )}

      {/* ─── Story section ────────────────────────────────────────────── */}
      {show.story && (
      <Section id="story" className="sf-section sf-tint-accent" style={getSectionStyle('story')} >
        <div ref={storyAnchor} />
        <div className="sf-container grid grid-cols-1 md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-2">
            <div
              className="aspect-[3/4] sf-radius-lg overflow-hidden shadow-xl"
              style={{
                background: copy.storyImage
                  ? `url(${copy.storyImage}) center/cover`
                  : `linear-gradient(160deg, ${primary} 0%, ${heroBg} 100%)`,
              }}
            >
              {!copy.storyImage && (
                <div className="w-full h-full grid place-items-center text-white/40">
                  <Utensils className="w-16 h-16" strokeWidth={1.2} />
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="sf-eyebrow">{copy.storyEyebrow}</span>
            <h2 className="sf-h2 mb-6">
              {copy.storyHeadline}{' '}
              <span className={phraseCls}>
                {copy.storyHeadlineItalic}
              </span>
            </h2>
            {copy.storyBodyHtml ? (
              <div
                className="sf-prose max-w-none"
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: copy.storyBodyHtml }}
                data-testid="mealpass-store-about"
              />
            ) : (
              <div className="sf-prose">
                <p>
                  A meal pass is simpler than it sounds. You pay upfront for a handful of meals at a
                  small discount, and then — whenever hunger, laziness, or a craving strikes — you
                  walk in, pick up, or have it delivered, all covered.
                </p>
                <p>
                  No apps, no drivers taking a cut, no expiring points. Just{' '}
                  <span style={{ color: primary, fontWeight: 600 }}>food you already own</span>{' '}
                  from the kitchen you love.
                </p>
              </div>
            )}
          </div>
        </div>
      </Section>
      )}

      {/* ─── Food photo gallery ───────────────────────────────────────── */}
      {show.gallery && (
      <Section className="sf-section" id="gallery" style={getSectionStyle('gallery')} data-testid="mealpass-store-gallery">
        <div className="sf-container">
          <div className="sf-section-head">
            <span className="sf-eyebrow">{copy.galleryEyebrow}</span>
            <h2 className="sf-h2">
              {copy.galleryHeadline}{' '}
              <span className={phraseCls}>{copy.galleryHeadlineItalic}</span>
            </h2>
          </div>
          {/* Gallery grid — magazine-style bento layout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {copy.galleryItems.slice(0, 8).map((item, i) => (
              <motion.button
                key={i}
                type="button"
                onClick={() => setLightboxIdx(i)}
                aria-label={item.caption ? `View photo: ${item.caption}` : `View photo ${i + 1}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ scale: 1.02 }}
                className={`relative overflow-hidden rounded-xl group cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                  Number(i) < 1 ? 'col-span-2 row-span-2 aspect-square md:aspect-auto' : 'aspect-square'
                }`}
                data-testid={`mealpass-store-gallery-item-${i}`}
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.caption || ''}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-full h-full grid place-items-center"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${heroBg} 100%)` }}
                  >
                    <Utensils className="w-8 h-8 text-white/40" />
                  </div>
                )}
                {item.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                    <p
                      className="text-white text-sm md:text-base leading-tight"
                      style={{ fontFamily: headingFontStack, fontStyle: 'italic', fontWeight: 400 }}
                    >
                      {item.caption}
                    </p>
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </Section>
      )}

      {/* ─── Testimonials ─────────────────────────────────────────────── */}
      {show.testimonials && (
      <Section className="sf-section sf-surface-white" id="testimonials" style={getSectionStyle('testimonials')} data-testid="mealpass-store-testimonials">
        <div className="sf-container">
          <div className="sf-section-head">
            <span className="sf-eyebrow">{copy.testEyebrow}</span>
            <h2 className="sf-h2">
              {copy.testHeadline}{' '}
              <span className={phraseCls}>{copy.testHeadlineItalic}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {copy.testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="sf-card p-8 relative"
              >
                <span aria-hidden="true" className="absolute top-5 right-7 sf-script text-6xl leading-none select-none" style={{ color: 'var(--sf-primary)', opacity: 0.25 }}>&rdquo;</span>
                <div className="flex gap-1 mb-4" style={{ color: primary }}>
                  {Array.from({ length: Math.min(Math.max(Number(t.rating) || 5, 1), 5) }).map((_, s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                </div>
                <p
                  className="text-lg leading-relaxed mb-6"
                  style={{ fontFamily: headingFontStack, fontStyle: 'italic', fontWeight: 400 }}
                >
                  "{t.quote}"
                </p>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: 'var(--sf-ink-muted)' }}>
                  — {t.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>
      )}

      {/* ─── Poetic punchline ─────────────────────────────────────────── */}
      {show.punchline && (
      <Section
        className="sf-section text-center relative overflow-hidden sf-dark-slab sf-on-dark sf-slab"
        id="punchline"
        style={getSectionStyle('punchline')}
        data-testid="mealpass-store-punchline"
      >
        <div className="absolute inset-0 pointer-events-none text-white/20">
          <FloatingDecoration Icon={Ticket} size={60} top="12%" left="10%" delay={0} duration={24} rotateFrom={-14} rotateTo={12} opacity={0.28} />
          <FloatingDecoration Icon={Sparkles} size={40} top="70%" right="14%" delay={2} duration={22} rotateFrom={16} rotateTo={-10} opacity={0.28} />
          <FloatingDecoration Icon={Utensils} size={50} top="60%" left="18%" delay={4} duration={26} rotateFrom={-8} rotateTo={14} opacity={0.24} />
        </div>
        <div className="relative max-w-3xl mx-auto px-6">
          <h2
            className="sf-display"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
          >
            {copy.punchlineHeadline}{' '}
            <span className={phraseCls}>
              {copy.punchlineHeadlineItalic}
            </span>
          </h2>
          <p className="mt-6 max-w-lg mx-auto text-lg opacity-75">
            {copy.punchlineBody}
          </p>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => scrollTo(programsAnchor)}
            className="mt-10 sf-btn sf-btn-on-dark"
            data-testid="mealpass-store-punchline-cta"
          >
            {copy.punchlineCTA}
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </Section>
      )}

      {/* ─── FAQ ──────────────────────────────────────────────────────── */}
      {show.faq && (
      <Section className="sf-section sf-tint" id="faq" style={getSectionStyle('faq')} data-testid="mealpass-store-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="sf-section-head">
            <span className="sf-eyebrow">{copy.faqsEyebrow}</span>
            <h2 className="sf-h2">
              {copy.faqsHeadline}{' '}
              <span className={phraseCls}>{copy.faqsHeadlineItalic}</span>
            </h2>
          </div>
          <div className="sf-faq">
            {faqs.map((f, i) => {
              const open = openFaqIndex === i;
              return (
                <motion.div
                  key={i}
                  initial={false}
                  data-testid={`mealpass-store-faq-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(open ? -1 : i)}
                    className="w-full text-left py-5 md:py-6 flex items-center justify-between gap-4 group"
                  >
                    <span
                      className="text-base md:text-lg"
                      style={{ fontFamily: headingFontStack, fontWeight: 600, color: open ? 'var(--sf-primary)' : 'var(--sf-ink)' }}
                    >
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: open ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className="w-5 h-5 text-neutral-400 group-hover:text-neutral-600 transition-colors" />
                    </motion.span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: open ? 'auto' : 0,
                      opacity: open ? 1 : 0,
                    }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-5 md:pb-6 leading-relaxed pr-10 text-[15px]" style={{ fontFamily: bodyFontStack, color: 'var(--sf-ink-muted)' }}>
                      {f.a}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>
      )}

      {/* ─── Big closing brand slab ───────────────────────────────────── */}
      {show.closingSlab && (
      <Section className="sf-section text-center sf-dark-slab sf-on-dark sf-slab" id="closing" style={getSectionStyle('closing')} data-testid="mealpass-store-closing">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.4em] uppercase mb-6 opacity-50">
            {copy.closingSince}
          </p>
          <h2
            className="sf-display"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
          >
            {copy.closingHeadline}{' '}
            <span className={phraseCls}>
              {copy.closingHeadlineItalic}
            </span>
          </h2>
          <div className="mt-10 flex items-center justify-center gap-4 text-sm opacity-60">
            <MapPin className="w-4 h-4" />
            <span className="tracking-widest uppercase text-xs">{brand.brand_name}</span>
          </div>
        </div>
      </Section>
      )}

      {/* ─── Footer ───────────────────────────────────────────────────── */}
      {/* ─── Delivery checker (placement: footer) ─────────────────────── */}
      {deliveryCheckerPlacement === 'footer' && deliveryCheckerBlock}

      <footer id="footer" className="py-12 sf-on-dark sf-surface-dark" style={getSectionStyle('footer')} data-testid="mealpass-store-footer">
        <div className="max-w-6xl mx-auto px-6 space-y-8">
          {/* Contact block — only rendered when merchant has provided contact info */}
          {(brand.contact_address || brand.contact_phone || brand.contact_email || brand.contact_hours) && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-xs pt-2">
              {brand.contact_address && (
                <div>
                  <div className="tracking-[0.3em] uppercase opacity-50 mb-1">Visit</div>
                  <div className="opacity-90 whitespace-pre-line">{brand.contact_address}</div>
                  {brand.google_maps_url && (
                    <a href={brand.google_maps_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 opacity-80 hover:opacity-100 inline-block mt-1">
                      Get directions →
                    </a>
                  )}
                </div>
              )}
              {brand.contact_hours && (
                <div>
                  <div className="tracking-[0.3em] uppercase opacity-50 mb-1">Hours</div>
                  <div className="opacity-90 whitespace-pre-line">{brand.contact_hours}</div>
                </div>
              )}
              {(brand.contact_phone || brand.contact_email) && (
                <div>
                  <div className="tracking-[0.3em] uppercase opacity-50 mb-1">Contact</div>
                  {brand.contact_phone && <div className="opacity-90">{brand.contact_phone}</div>}
                  {brand.contact_email && <div className="opacity-90">{brand.contact_email}</div>}
                </div>
              )}
              {(brand.instagram_url || brand.facebook_url || brand.website_url) && (
                <div>
                  <div className="tracking-[0.3em] uppercase opacity-50 mb-1">Follow</div>
                  <div className="flex flex-col gap-1">
                    {brand.website_url && <a href={brand.website_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 opacity-80 hover:opacity-100">Website</a>}
                    {brand.instagram_url && <a href={brand.instagram_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 opacity-80 hover:opacity-100">Instagram</a>}
                    {brand.facebook_url && <a href={brand.facebook_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 opacity-80 hover:opacity-100">Facebook</a>}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
            <div>
              <span className="opacity-90 text-base" style={{ fontFamily: headingFontStack, fontWeight: 600 }}>
                {brand.brand_name}
              </span>
              {brand.tagline && <span className="ml-3 opacity-70">· {brand.tagline}</span>}
            </div>
            <div className="text-xs tracking-widest uppercase opacity-70">
              Powered by <span className="text-white/90">Opsy</span>
            </div>
          </div>
        </div>
      </footer>

      {/* v5.10 — free (Launch) tenants carry a subtle "Powered by PlateOS.io"
          watermark: free branding in exchange for $0 software fees. */}
      {poweredByPlateos && (
        <a
          href="https://plateos.io"
          target="_blank"
          rel="noreferrer"
          data-testid="powered-by-plateos-watermark"
          className="fixed bottom-3 left-3 md:left-auto md:right-3 z-40 flex items-center gap-1.5 rounded-full
                     bg-black/60 px-3 py-1.5 text-[11px] font-medium text-white/80
                     backdrop-blur-sm shadow-lg ring-1 ring-white/15 transition
                     hover:bg-black/75 hover:text-white"
        >
          <span aria-hidden className="text-teal-300">⚡</span>
          Powered by <span className="font-semibold text-white">PlateOS.io</span>
        </a>
      )}

      {showPurchaseModal && selectedProgram && (
        <MealPassPurchaseModal
          program={selectedProgram}
          open={showPurchaseModal}
          onClose={() => {
            setShowPurchaseModal(false);
            // If the tenant has add-ons available AND the buyer just added a pass,
            // offer them the cinematic upsell — same UX as meal plans.
            setTimeout(() => {
              const cartGrew = (cartItems?.length || 0) > preAddCartCount;
              if (cartGrew && availableAddons.length > 0) {
                setShowAddonsOffer(true);
              }
            }, 200);
          }}
          onComplete={() => { setShowPurchaseModal(false); }}
          darkMode={darkMode}
          brandingHint={{ primary_color: primary, accent_color: accent }}
          brand={brand}
          storeSlug={storeSlug}
          mealItems={mealItems}
        />
      )}

      {/* Meal plan tier/add-ons/start-date modal — legacy AddToCartModal reused so the
          full checkout flow (tier picker → add-ons → delivery days → start date → cart)
          works from the unified storefront just like it did on the legacy CustomerStorefront.
          Cinematic brand palette flows in via `brandingHint` — the modal re-themes its
          teal utility classes to the merchant's primary color via scoped CSS. */}
      {selectedMealPlan && (
        <AddToCartModal
          subdomain={getSubdomain()}
          mealPlan={selectedMealPlan}
          storeSlug={storeSlug}
          storeId={selectedMealPlan?.store_ids?.[0] || null}
          brandingHint={{ primary_color: primary, accent_color: accent }}
          // v4.12 — GUIDED ADD-ON WIZARD: the modal runs plan → Next → add-ons
          // → checkout in ONE flow (replacing the old modal ping-pong). The
          // store's add-ons ride in; the modal configures + adds them itself,
          // then navigates straight to checkout.
          availableAddons={availableAddons}
          onCheckout={() => {
            setSelectedMealPlan(null);
            navigate(checkoutDest());
          }}
          onClose={() => setSelectedMealPlan(null)}
          brand={brand}
          photo={planPhotoOf(selectedMealPlan)}
        />
      )}

      {/* Catering event configurator (v4.24): configure event → premium
          upsells → cart → checkout. The backend re-prices and re-validates
          the event slot fail-closed at checkout — the modal's math and
          lead-time checks are UX only. */}
      {selectedCateringPackage && (
        <CateringOrderModal
          package={selectedCateringPackage}
          availability={cateringAvailability || undefined}
          brandPrimary={primary}
          itemsById={(() => {
            // Build a combined lookup: meal items + catering dish images fallback.
            const m = {};
            mealItems.forEach((i) => { m[i.id] = i; });
            // Enrich from cateringDishImages for items not in the meal-items feed
            // (e.g. catering-only tenants where meal items lack pricing).
            Object.entries(cateringDishImages).forEach(([id, img]) => {
              if (!m[id]) m[id] = img;
              else if (!m[id].image_url && img.image_url) m[id] = { ...m[id], ...img };
            });
            return m;
          })()}
          onClose={() => setSelectedCateringPackage(null)}
          onSubmit={(options) => {
            addCateringToCart(selectedCateringPackage, {
              ...options,
              storeSlug: storeSlug || '',
              storeId: selectedCateringPackage?.store_ids?.[0] || null,
              storeName: tenantMeta?.name || '',
            });
            setSelectedCateringPackage(null);
            navigate(checkoutDest());
          }}
          brand={brand}
        />
      )}

      {/* Meal-PASS purchase upsell (separate from the meal-plan wizard above):
          after buying a prepaid pass, offer the store's add-ons. v4.12 — the
          loop is gone: selecting an add-on opens ONE config modal that goes
          straight to checkout (no bouncing back to the offer). */}
      <AddonsOfferModal
        open={showAddonsOffer}
        onClose={() => setShowAddonsOffer(false)}
        addons={availableAddons}
        brandingHint={{ primary_color: primary, accent_color: accent }}
        onSelectAddon={(addon) => {
          setShowAddonsOffer(false);
          setAddonSubjectPlan(addon);
        }}
        onContinueToCheckout={() => {
          setShowAddonsOffer(false);
          navigate(checkoutDest());
        }}
        brand={brand}
        />
      {addonSubjectPlan && (
        <AddToCartModal
          subdomain={getSubdomain()}
          mealPlan={addonSubjectPlan}
          storeSlug={storeSlug}
          storeId={addonSubjectPlan?.store_ids?.[0] || null}
          brandingHint={{ primary_color: primary, accent_color: accent }}
          onCheckout={() => {
            setAddonSubjectPlan(null);
            navigate(checkoutDest());
          }}
          onClose={() => setAddonSubjectPlan(null)}
          brand={brand}
          photo={planPhotoOf(addonSubjectPlan)}
        />
      )}

      {/* v4.12 — "Chat with OpSy" on the storefront: the same floating
          widget the dashboard mounts. Gated on a live customer session
          (the widget's API calls need the session token — anonymous
          visitors get the header "Ask {name}" login CTA instead) and
          hidden in the Design Studio preview iframe (no session there;
          a dead launcher would just clutter the canvas). */}
      {isAuthed && !isPreview && (
        <OpsyCustomerChat storeId={storeSlug} />
      )}
      {/* v4.14 — anonymous visitors get a REAL bottom chat bubble too:
          the support widget (FAQs + live ticket chat, no session needed).
          On mobile this replaces the cramped header CTA as the chat entry
          point; authed visitors keep the OpSy widget above instead. */}
      {!isAuthed && !isPreview && (
        <SupportChatWidget
          subdomain={getSubdomain()}
          storeId={storeSlug || null}
          mode="openChat"
          agentName={opsyName || 'OpSy'}
        />
      )}
    </div>
  );
};

export default MealPassProgramStore;
